/*
 * fyre-resolve.js — Model F brake-system builder: dynamic, data-driven resolution on the golden PDP.
 *
 * The umbrella is a thin shell. Everything the customer sees — selectable options, the gallery image, the
 * price, the real SKU — is pulled LIVE from the backing products via the Storefront API. Nothing about the
 * options/images is hardcoded on the umbrella; the umbrella's picker is just the chrome.
 *
 * Flow:
 *   1. Graph (once): umbrella custom.builder_collections -> builder collection -> backing products, each with
 *      fitment.groups -> group.vehicles. Builds the vehicle-handle -> backing map.
 *   2. On vehicle change: pick the matched backing, fetch its FULL variant matrix once (options + image + price
 *      + availableForSale + quantityAvailable), cache it. Disable picker option-values the matched backing
 *      doesn't offer.
 *   3. On option change: resolve the variant from the cached matrix -> write the real backing variant id into
 *      the cart form, swap the gallery to that variant's image, show its SKU + price. Fail-closed otherwise.
 *      Custom Color resolves to a real but no-SKU/unfulfillable variant — the cart id is swapped to its
 *      highest-inventory real-color sibling (see bestRealColorVariant), failing closed if none is in stock.
 *
 * Resolution runs against the Storefront API with a public (client-exposed) token — there is no static asset.
 * Fail-closed: the form id is blanked on every change and only re-filled on a clean resolve; the ADD-TO-CART
 * button stays disabled until a clean resolve enables it.
 *
 * B2B pricing: the Storefront API (public token, @inContext country) is buyer-blind — it returns RETAIL for every
 * variant regardless of who is logged in — so for a B2B buyer, per-variant prices are overlaid from the buyer-aware
 * /products/<handle>.js endpoint (the same source the B2B quick-order form uses). A logged-in dealer gets their
 * catalog price; everyone else gets retail. Overlaid prices are in-memory only and refreshed every session — the
 * persisted cache holds only buyer-invariant structure + retail price — so a dealer price can never leak into a
 * retail session nor go stale after a catalog change. On any .js failure it degrades to the Storefront retail price.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-fyre-resolve]');
  if (!root) return;
  var TOKEN = root.dataset.sfToken;
  var COLL_NUM = (root.dataset.collection || '').replace(/\D/g, '');
  if (!TOKEN || !COLL_NUM) return; // not a configured builder umbrella -> fail-to-native (no-op)

  var API = '/api/' + (root.dataset.sfVersion || '2026-04') + '/graphql.json';
  var COLLECTION_GID = 'gid://shopify/Collection/' + COLL_NUM;

  // picker (umbrella) option name -> backing option name. matrix matches name+value EXACTLY, so picker picks
  // are normalized to the backing's vocab before matching/availability.
  var NAME_TO_BACKING = { 'finish': 'Caliper Color', 'color': 'Caliper Color', 'colour': 'Caliper Color', 'diameter': 'Disc Diameter', 'disc type': 'Disc Type', 'disc face': 'Disc Type' };
  // diameter is a PRODUCT-split axis in Model F (one backing per diameter), but the picker shows it with an 'mm'
  // suffix ("380mm") while the backing's specs.disc_diameter metafield is bare ("380"). Normalize both to digits.
  function normDia(s) { return s ? String(s).replace(/[^0-9.]/g, '') : ''; }

  var graph = null;            // [{ id, diameter:'380', vehicles:{handle:true}, groupIds:[...] }]
  var matrixCache = {};        // backingId -> STRUCTURAL matrix (options/image/specs + Storefront RETAIL price). Buyer-INVARIANT → safe to persist.
  var matrixHandle = {};       // backingId -> product handle, for the buyer-price overlay via /products/<handle>.js. Persisted with matrixCache.
  var pricedCache = {};        // backingId -> matrix with buyer-aware prices overlaid live. IN-MEMORY ONLY, never persisted (no cross-session leak / staleness).
  var padCache = {};           // backingId -> [{title,sku,variantId,price}] from the backing's content.pad_products. IN-MEMORY ONLY (mirrors pricedCache) — pad data stays OFF the persisted matrix so it needs no CACHE_KEY bump.
  var padSeq = 0;              // generation guard: a late pad fetch for a superseded backing must NOT repaint the picker (fast diameter switch).
  var padsReadyFor = null;     // backing id whose pads are currently painted — ATC is gated on this === activeBacking.id so a warm-matrix resolve can't enable purchase before the resolved backing's pad paints (stale-harvest race).
  var atcHeldForPads = false;  // a buyable variant resolved but ATC is held until its pads paint; the pad fetch's success (or persistent-fail) releases it.
  var discProductCache = {};      // backingId -> product-level content.disc_product card (or null)
  var fitmentDiagramCache = {};   // backingId -> fitment diagram image URL (or null)
  var backingSpecsCache = {};     // backingId -> backing-constant disc specs {disc_diameter,disc_thickness,disc_material,disc_construction,disc_finish,disc_mounting} (T1 tech-spec render fallback)
  var companionHost = document.querySelector('[data-pdp-companion]'); // "Complete Your Brake System" host (builder umbrellas)
  // The companion needs no cache of its own — the paired rear rides each matrix row (.companion), persisted via matrixCache.
  var activeBacking = null;
  var representativeMatrix = null; // matrix used for the no-vehicle gallery/swatches
  var resolveSeq = 0;
  // true while the resolved-variant VIEW gallery occupies the PDP's single left thumb strip. The PDP has exactly
  // one gallery; the view thumbs overtake it on a resolve and hand it back to the disc-type switcher when there
  // is nothing to switch between. See populateViewThumbs / populateThumbnails.
  var viewThumbsActive = false;
  var currentSig = null;       // signature of the cached graph (collection product ids + updatedAt)
  // B2B buyer signal — the logged-in company-location id (empty for guest/retail), rendered by Liquid. Used ONLY to
  // decide whether to overlay buyer-aware prices. Deliberately NOT part of CACHE_KEY: the persisted cache holds only
  // buyer-INVARIANT structure + Storefront retail price; buyer prices are overlaid live per session and never persisted,
  // so they can neither leak across sessions nor go stale after a catalog-% change.
  var IS_B2B = !!(root.dataset.buyer || '');
  // RT Product Feed gate — a B2B buyer with no location selected still has data-buyer empty, so IS_B2B above
  // (a "overlay dealer prices?" signal) cannot answer "may this buyer see B2B-only SKUs?". Separate signal.
  // Read the resolver div first, then the body stamp from layout/theme.liquid — one signal, two emitters, so a
  // page that mounts the resolver without the attribute still gets the right answer instead of silently retail.
  var IS_B2B_CUSTOMER = (root.dataset.b2b || (document.body && document.body.dataset.b2b)) === 'true';
  // localStorage cache key. Bump the version segment whenever the SHAPE of the persisted graph/matrix changes —
  // an old payload has no schema marker of its own, so the key is the only thing that invalidates it.
  // v12: backingSpecsCache gained kit_includes + short_description, and the compat panel narrows to the
  // resolved backing — a v11 cache would restore without either, so the version must move with the shape.
  // v13: each graph node carries groupIds instead of groupNames. A restored v12 node has neither, so the compat
  // narrowing would find no groups and paint the "unavailable" state on every builder umbrella.
  // v14: matrix rows gained qty (quantityAvailable) for the Custom Color -> highest-inventory real-color swap.
  // v15: the matrix is RT-Product-Feed filtered, so it is no longer buyer-invariant — a v14 payload holds rows
  // this buyer may not see. The persisted b2b flag (below) guards the same shape across a login/logout swap.
  // BUMP THIS whenever a persisted field changes MEANING, not just when one is added. v16: the matrix `image`
  // field became the output of ownsItsPhoto rather than the raw Storefront url, so every v15 cache replays a
  // drilled photo under an unphotographed face — the defect that fix was for, served from localStorage.
  // v18: backingSpecsCache gained caliper_drawing/disc_drawing, caliper_body_type and bleed_screw; a v16 entry lacks
  // them and would pin the umbrella's values.
  var CACHE_KEY = 'fyre-builder:v18:' + COLL_NUM;

  // timeoutMs (optional): abort a HUNG request (socket open, no response — never rejects on its own) so it degrades
  // to a rejection the caller can retry/fail instead of hanging forever. Only passed where a hung fetch would block
  // UI (the pad query gates ATC). Omitted → no abort signal and no timer.
  function sf(query, variables, timeoutMs) {
    var ctrl = (timeoutMs && typeof AbortController !== 'undefined') ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, timeoutMs) : null;
    var opts = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': TOKEN },
      body: JSON.stringify({ query: query, variables: variables || {} })
    };
    if (ctrl) opts.signal = ctrl.signal;
    return fetch(API, opts).then(function (r) { return r.json(); }).then(function (j) {
      if (timer) clearTimeout(timer);
      if (j.errors) throw new Error(JSON.stringify(j.errors));
      return j.data;
    }, function (e) { if (timer) clearTimeout(timer); throw e; }); // abort/network reject -> propagate to caller's retry/fail
  }

  function ambientVehicleHandle() {
    var v = null;
    try { v = window.FitmentStorage ? window.FitmentStorage.get() : JSON.parse(localStorage.getItem('fitment_vehicle') || 'null'); } catch (e) { v = null; }
    return v && (v.id || v.handle) ? (v.id || v.handle) : '';
  }

  function optionRadios() { return Array.prototype.slice.call(document.querySelectorAll('[data-pdp-option-radio]')); }
  function backingOptName(r) { var m = /options\[(.+?)\]/.exec(r.name); return m ? (NAME_TO_BACKING[m[1].toLowerCase()] || m[1]) : ''; }
  function isDiameterRadio(r) { return backingOptName(r) === 'Disc Diameter'; }
  function selections() {
    var sel = {};
    optionRadios().forEach(function (r) { if (r.checked) { var m = /options\[(.+?)\]/.exec(r.name); if (m) sel[m[1]] = r.value; } });
    return sel;
  }
  // map current picker picks -> { backingOptionName: value } in the backing's vocab
  function mappedPicks() {
    var sel = selections(), out = {};
    Object.keys(sel).forEach(function (name) {
      var backingName = NAME_TO_BACKING[name.toLowerCase()] || name;
      var backingValue = sel[name];
      out[backingName] = backingValue;
    });
    return { opts: out };
  }

  function theForm() { return document.querySelector('.pdp-form-shell') || document.querySelector('form[action*="/cart/add"]'); }
  function atcBtn() { return document.querySelector('[data-pdp-atc]'); }
  function numericId(gid) { var m = /(\d+)$/.exec(String(gid)); return m ? m[1] : ''; }
  function setVariantId(id) { var f = theForm(); if (f) { var i = f.querySelector('[name="id"]'); if (i) i.value = id || ''; } }
  // setAtc also owns [data-atc-sub]: on builder umbrellas fyre-pdp.js's applyFitmentState is skipped (isResolver),
  // so the sub-line otherwise keeps its server-rendered "IN STOCK — SHIPS…" default even under OUT OF STOCK /
  // DOES NOT FIT / SELECT VEHICLE. Hide it whenever the button is disabled (the main text carries the reason);
  // restore it only on a clean resolve.
  function setAtc(disabled, text) {
    var b = atcBtn(); if (!b) return;
    b.disabled = disabled;
    var t = b.querySelector('[data-atc-text]'); if (t && text) t.textContent = text;
    var s = b.querySelector('[data-atc-sub]');
    if (s) { if (disabled) { s.hidden = true; } else { s.hidden = false; s.textContent = 'IN STOCK — SHIPS WITHIN 2 BUSINESS DAYS'; } }
  }
  function setSku(sku) { var el = document.querySelector('[data-pdp-sku]'); if (el) el.textContent = sku ? 'SKU: ' + sku : ''; }
  // umbrella's own server-rendered price/SKU (base variant) — snapshotted ONCE before any resolve overwrites them,
  // so removing the vehicle (or losing fit) can restore the pre-resolution display instead of leaving the last
  // resolved backing's price/SKU stuck on screen.
  var priceDefaultHTML = null, skuDefaultText = null, affirmDefaultAmount = null;
  function snapshotPriceSkuDefaults() {
    if (priceDefaultHTML !== null) return;
    var priceEl = document.querySelector('.pdp-price'); if (priceEl) priceDefaultHTML = priceEl.innerHTML;
    var skuEl = document.querySelector('[data-pdp-sku]'); if (skuEl) skuDefaultText = skuEl.textContent;
    var af = document.querySelector('.affirm-as-low-as'); if (af) affirmDefaultAmount = af.getAttribute('data-amount');
  }
  function restorePriceSkuDefaults() {
    snapshotPriceSkuDefaults();
    lastResolvedPrice = null; padDelta = 0;
    var priceEl = document.querySelector('.pdp-price'); if (priceEl && priceDefaultHTML !== null) priceEl.innerHTML = priceDefaultHTML;
    var skuEl = document.querySelector('[data-pdp-sku]'); if (skuEl && skuDefaultText !== null) skuEl.textContent = skuDefaultText;
    var af = document.querySelector('.affirm-as-low-as');
    if (af && affirmDefaultAmount !== null) {
      af.setAttribute('data-amount', affirmDefaultAmount);
      if (window.affirm && window.affirm.ui && typeof window.affirm.ui.refresh === 'function') window.affirm.ui.refresh();
    }
  }
  function money(p) { try { return Number(p.amount).toLocaleString('en-US', { style: 'currency', currency: p.currencyCode || 'USD' }); } catch (e) { return '$' + p.amount; } }
  // The resolved backing's OWN price, and the net effect of the current pad choice on top of it. The kit
  // price includes specs.default_pad_set, so swapping compounds moves what the buyer pays by the difference
  // between the two pads (FyrePadPicker in fyre-pdp.js computes it and announces it as 'fyre:pad-delta').
  // Held separately so a later pad change can re-render the price without re-resolving the backing.
  var lastResolvedPrice = null, padDelta = 0;
  function setPrice(p) {
    if (p) lastResolvedPrice = p;
    if (!p) return;
    // The resolver is the last price writer on umbrellas, so it must carry the Custom paint surcharge (cents, from
    // fyre-pdp.js) or the PDP shows less than the cart charges after a toggle. It stacks with the pad delta —
    // both are adjustments on the resolved backing's own price, and a buyer can make both choices at once.
    var sur = (customPaintActive() && window.FyrePdpForm && window.FyrePdpForm.getCustomColorSurcharge) ? window.FyrePdpForm.getCustomColorSurcharge() : 0;
    var adjusted = { amount: String(Number(p.amount) + padDelta + sur / 100), currencyCode: p.currencyCode };
    var el = document.querySelector('.pdp-price'); if (el) el.innerHTML = '<span class="pdp-price__regular font-secondary">' + money(adjusted) + '</span>';
    // keep Affirm's "as low as" estimate in sync with the RESOLVED variant price — the umbrella renders a flat base
    // data-amount (a wrong monthly for every non-base config). Update the amount (cents) + refresh the widget when
    // the SDK is present; harmless no-op if it isn't (the SDK loads theme-wide on PDP — see layout/theme.liquid).
    // Fail-closed reverts this to the umbrella's own base amount — see restorePriceSkuDefaults().
    var af = document.querySelector('.affirm-as-low-as');
    if (af) {
      var cents = Math.round(Number(adjusted.amount) * 100);
      if (cents) af.setAttribute('data-amount', String(cents));
      if (window.affirm && window.affirm.ui && typeof window.affirm.ui.refresh === 'function') window.affirm.ui.refresh();
    }
  }

  // ── Line-item properties ── surface the resolved config + vehicle on the cart line. The cart adds the
  // BACKING variant (real SKU), so without these it shows the backing's identity, not the customer's picks.
  // fyre-pdp.js FyreATC.submit() harvests every enabled [name^="properties["] input inside [data-pdp-section]
  // (root sits within it), so we inject enabled hidden inputs and let submit() collect them — no separate cart
  // call. The backing SKU already encodes color/diameter/disc for NetSuite; these are human-readable descriptors.
  // Re-set on every clean resolve, cleared on every fail-closed so a stale config can never ride the cart line.
  function lipHost() {
    var h = root.querySelector('[data-fyre-lip]');
    if (!h) { h = document.createElement('span'); h.setAttribute('data-fyre-lip', ''); root.appendChild(h); }
    return h;
  }
  function clearLineItemProps() { var h = root.querySelector('[data-fyre-lip]'); if (h) h.innerHTML = ''; }
  function setLineItemProps(pairs) {
    lipHost().innerHTML = Object.keys(pairs).filter(function (k) { return pairs[k]; }).map(function (k) {
      return '<input type="hidden" name="properties[' + k + ']" value="' + esc(pairs[k]) + '">';
    }).join('');
  }
  function fitmentDisplay() {
    var v = null;
    try { v = window.FitmentStorage ? window.FitmentStorage.get() : JSON.parse(localStorage.getItem('fitment_vehicle') || 'null'); } catch (e) { v = null; }
    return v ? (v.display || [v.year, v.make, v.model, v.submodel].filter(Boolean).join(' ')) : '';
  }

  // append a Shopify CDN resize param (the umbrella has no media of its own; all images come from backings)
  function sizedUrl(url, w) { if (!url) return url; return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w; }

  // swap the gallery main image to the resolved backing-variant's image (umbrella has none of its own).
  // Update the existing <img> in place rather than replacing the node — fyre-pdp.js's FyreGallery caches
  // a reference to it at init(), and swapping the node would leave that reference stale/detached, silently
  // breaking the static thumbnail click handlers.
  // Branded "Image Coming Soon", handed in by the section (asset_img_url is Liquid-only). Cody, 2026-09-11:
  // a face with no photograph of its own must say so, not borrow a neighbouring face's photo — a borrowed
  // photo is a wrong answer wearing a right one's clothes, and the shopper cannot tell.
  function placeholderUrl() {
    var host = document.querySelector('[data-fyre-resolve]');
    return (host && host.getAttribute('data-placeholder-image')) || '';
  }
  // Paint the placeholder and drop the thumb strip: the strip belongs to a config we are declining to show.
  // Returns false when no placeholder asset was supplied, so callers can fall back to their old behaviour
  // rather than blanking the gallery.
  function setGalleryPlaceholder() {
    var url = placeholderUrl();
    if (!url) return false;
    var main = document.querySelector('.pdp-gallery__main');
    if (!main) return false;
    main.innerHTML = '<img class="pdp-gallery__main-img" data-pdp-main-image src="' + url +
      '" alt="Image coming soon" loading="eager">';
    var gallery = document.querySelector('[data-pdp-gallery]');
    var strip = gallery && gallery.querySelector('[data-fyre-thumbs]');
    if (strip && strip.parentNode) strip.parentNode.removeChild(strip);
    viewThumbsActive = false;
    return true;
  }

  function setGalleryImage(url) {
    var main = document.querySelector('.pdp-gallery__main');
    if (!main) return;
    if (!url) return;
    var img = main.querySelector('[data-pdp-main-image]');
    if (!img) {
      main.innerHTML = '<img class="pdp-gallery__main-img" data-pdp-main-image src="' + sizedUrl(url, 900) + '" alt="" loading="eager">';
      return;
    }
    img.removeAttribute('srcset');
    var source = img.closest('picture') && img.closest('picture').querySelector('source[type="image/webp"]');
    if (source) source.removeAttribute('srcset');
    img.src = sizedUrl(url, 900);
  }

  // CALIPER COLOR SWATCHES: the Finish picker renders grey placeholders because the umbrella has no media.
  // Inject the backing's real per-color caliper photo into each color swatch (data-driven, no umbrella images).
  function populateSwatches(matrix) {
    if (!matrix) return;
    var imgByColor = {};
    matrix.forEach(function (v) { var c = v.opts['Caliper Color']; if (c && v.image && !imgByColor[c]) imgByColor[c] = v.image; });
    optionRadios().forEach(function (r) {
      var m = /options\[(.+?)\]/.exec(r.name); if (!m) return;
      if ((NAME_TO_BACKING[m[1].toLowerCase()] || m[1]) !== 'Caliper Color') return; // only the color/finish swatches
      var url = imgByColor[r.value]; if (!url) return;
      var label = r.closest('label'); if (!label) return;
      var wrap = label.querySelector('.pdp-option__swatch-img-wrap'); if (!wrap) return;
      if (wrap.querySelector('.pdp-option__swatch-placeholder')) { // only replace a grey placeholder, never a custom/disc swatch
        wrap.innerHTML = '<img src="' + sizedUrl(url, 120) + '" alt="' + r.value + '" class="pdp-option__swatch-img" loading="lazy">';
      }
    });
  }

  // before a vehicle is chosen (or when none fits), show a representative caliper photo in the gallery —
  // matched to the current color + diameter + disc type picks where possible, else progressively looser.
  // Matching on color alone leaves the image stuck on one disc type while the TY1/Drilled thumbs toggle
  // active state with no visible change — confusing when "DOES NOT FIT YOUR VEHICLE" blocks resolution.
  //
  // THE THUMB STRIP IS THE VIEW GALLERY FIRST: the gallery renders properly with no fitment chosen at all. The
  // representative matrix is a FULL matrix (loadRepresentative -> loadMatrix), so its rows already carry
  // .images = [Main, PDP 2/3/4] — the shopper's photos are in hand at cold load and need no vehicle and no
  // umbrella-level media. The disc-type switcher survives ONLY as a fallback for a variant with fewer than 2
  // images (the ~175 backings whose variants carry no photos): a lone thumb is useless, and a bare gallery is
  // worse than the strip.
  //
  // A caliper colour with NO photographed variant cannot show itself. Gold on the GT|A front 1-piece umbrella
  // is the standing case: no backing variant in any face carries a photo, while Red, Black and Silver are
  // photographed throughout. A last-resort `matrix.filter(v => v.image)[0]` would pick whatever
  // variant happens to sit first in the array, so "the default colour" would really be API ordering and could
  // change with no data change at all. Instead an unphotographed pick holds the last colour that DID have
  // photos and reuses ITS gallery — continuity for the shopper, no new photography.
  var lastImagedColor = null;

  // "Has its own photos" must NOT be tested with v.image. The Storefront API falls variant.image back to the
  // PRODUCT's featured image when a variant has none, so in the browser every Custom variant reports an image
  // while the Admin API says all 92 are null. Testing v.image therefore classes Custom as photographed and the
  // hold-last-colour rule never fires. v.images carries [Main, content.pdp_image_2/3/4] from file_reference
  // metafields, which have NO fallback, so 2+ entries is the honest signal that a colour was actually shot.
  function colorIsShot(matrix, color) {
    return !!color && matrix.some(function (v) {
      return v.opts['Caliper Color'] === color && v.images && v.images.length > 1;
    });
  }

  // Deterministic stand-in for the cold case — Custom picked before any photographed colour has been seen,
  // so there is no "last" to hold. Colour with the most photographed variants wins; ties break alphabetically.
  // Deliberately NOT array order: that is the arbitrary behaviour this replaces.
  function fallbackColor(matrix) {
    var count = {};
    matrix.forEach(function (v) {
      var c = v.opts['Caliper Color'];
      if (c && v.images && v.images.length > 1) count[c] = (count[c] || 0) + 1;
    });
    return Object.keys(count).sort(function (a, b) { return count[b] - count[a] || a.localeCompare(b); })[0] || null;
  }

  // The umbrella picker's disc-face vocabulary and the backing's `Disc Type` values are authored separately, so a
  // picker "Cross Drilled" can face a backing "Drilled". sections/fyre-template-product.liquid collapses exactly
  // these pairs when it picks the swatch icon (cross-drilled -> drilled, type-1-ty1 / ty1 -> t1); mirror that
  // collapse so both sides normalise the same way. The loose compare is used ONLY when it cannot merge two
  // distinct picker values into one key — otherwise two real faces would silently share one photo.
  function discNorm(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
      .replace('cross-drilled', 'drilled').replace('type-1-ty1', 't1').replace(/^ty1$/, 't1');
  }
  function pickerDiscValues() {
    var out = [], seen = {};
    optionRadios().forEach(function (r) {
      if (backingOptName(r) !== 'Disc Type') return;
      var v = r.value;
      if (v && !seen[v]) { seen[v] = true; out.push(v); }
    });
    return out;
  }
  var discLooseOk = null;
  function looseDiscOk() {
    if (discLooseOk === null) {
      var keys = {}, ok = true;
      pickerDiscValues().forEach(function (v) { var k = discNorm(v); if (keys[k]) ok = false; keys[k] = true; });
      discLooseOk = ok;
    }
    return discLooseOk;
  }
  // The Storefront API answers variant.image with the PRODUCT's featured image when the variant carries none
  // (the Admin API returns null there). Every unphotographed face therefore reports a photo OF ANOTHER FACE,
  // which is #325 itself: TY3L rows returned the drilled shot, so the placeholder could never fire. A row owns
  // its photo when the URL differs from the product's featured image, or when the filename names the face this
  // row sells — that second clause is what keeps the one variant the featured image genuinely belongs to.
  var FACE_IN_FILENAME = [
    [/(?:slotted-)?type-3l\b/i, 'ty3l'], [/(?:slotted-)?type-1\b/i, 'ty1'],
    [/(?:slotted-)?type-3\b/i, 'ty3'], [/(?:slotted-)?type-5\b/i, 'ty5'],
    [/cross-drilled|drilled/i, 'drilled']
  ];
  // 'Full-Floating' and 'Plated' describe the disc's construction, not its face, and no photograph shows them.
  function faceKey(s) {
    return String(s || '').toLowerCase().replace(/\s*(full[-\s]?floating|plated)\s*/g, ' ').replace(/[^a-z0-9]+/g, '');
  }
  function ownsItsPhoto(url, featuredUrl, face) {
    if (!url) return false;
    if (url !== featuredUrl) return true;
    if (!face) return true;                       // hand-brake systems sell no disc face to contradict
    var hit = FACE_IN_FILENAME.filter(function (p) { return p[0].test(url); })[0];
    return !!hit && hit[1] === faceKey(face);
  }
  function discEq(rowValue, pick) {
    if (rowValue === pick) return true;
    return looseDiscOk() && discNorm(rowValue) === discNorm(pick);
  }
  // Faces the picker offers that no PHOTOGRAPHED row in this matrix can satisfy.
  function missingDiscFaces(matrix) {
    return pickerDiscValues().filter(function (face) {
      return !matrix.some(function (v) { return v.image && discEq(v.opts['Disc Type'], face); });
    });
  }

  function setInitialGallery(matrix) {
    if (!matrix) return;
    var picks = mappedPicks(), color = picks.opts['Caliper Color'], dia = picks.opts['Disc Diameter'], disc = picks.opts['Disc Type'];
    // Was the PICKED colour actually photographed? Only then is it the gallery's colour — and only then does
    // it become the "last" colour that a later unphotographed pick (Custom) holds on to.
    var pickedIsShot = colorIsShot(matrix, color);
    if (pickedIsShot) { lastImagedColor = color; }
    var galleryColor = pickedIsShot ? color : (lastImagedColor || fallbackColor(matrix));
    var hit;
    if (disc) {
      // A picked disc face OWNS the image: drop DIAMETER before dropping the face, and never fall through to a
      // colour-only or any-image row, which would contradict the pick. That fall-through is #325 — pre-vehicle,
      // every face redisplayed the drilled photo because the disc tier missed and the colour tier answered.
      hit = matrix.filter(function (v) {
          return v.opts['Caliper Color'] === galleryColor && (!dia || v.opts['Disc Diameter'] === dia) && discEq(v.opts['Disc Type'], disc) && v.image;
        })[0]
        || matrix.filter(function (v) { return v.opts['Caliper Color'] === galleryColor && discEq(v.opts['Disc Type'], disc) && v.image; })[0];
      // No photographed row for this face. Holding the current image silently shows a DIFFERENT face's
      // photo, which is what #325 was reported as in the first place — say "Image Coming Soon" instead.
      // Falls back to the old hold-in-place when the section supplied no placeholder asset.
      if (!hit) { setGalleryPlaceholder(); return; }
    } else {
      hit = matrix.filter(function (v) {
          return v.opts['Caliper Color'] === galleryColor && (!dia || v.opts['Disc Diameter'] === dia) && v.image;
        })[0]
        || matrix.filter(function (v) { return v.opts['Caliper Color'] === galleryColor && v.image; })[0]
        || matrix.filter(function (v) { return v.image; })[0];
    }
    if (hit && hit.image) setGalleryImage(hit.image);
    // Branch explicitly rather than letting populateViewThumbs fall through its own <2 path: on a COLD load that
    // path finds no strip it owns and returns without painting anything, which would leave the gallery bare.
    if (hit && hit.images && hit.images.length > 1) { populateViewThumbs(hit); return; }
    populateThumbnails(matrix);
  }

  // GALLERY THUMBNAILS: the umbrella carries no media, so build the thumb strip from the active backing's
  // variants for the CURRENT color (one per disc type, distinct images). Clicking a thumb selects that disc
  // type (re-resolves + swaps the main image).
  function selectDisc(value) {
    var radios = optionRadios();
    for (var i = 0; i < radios.length; i++) {
      var m = /options\[(.+?)\]/.exec(radios[i].name);
      if (m && (NAME_TO_BACKING[m[1].toLowerCase()] || m[1]) === 'Disc Type' && radios[i].value === value && !radios[i].checked) {
        radios[i].checked = true; radios[i].dispatchEvent(new Event('change', { bubbles: true })); return;
      }
    }
  }
  function populateThumbnails(matrix) {
    if (!matrix) return;
    // The resolved-variant view gallery OWNS the single left strip while it is up (see populateViewThumbs).
    // Bail here rather than at each call site: populateThumbnails runs from three places, one of them a
    // deferred setTimeout in the matrix load that lands AFTER the resolve has already painted view thumbs —
    // without this guard that late repaint silently replaces them with disc thumbs.
    if (viewThumbsActive) return;
    var gallery = document.querySelector('[data-pdp-gallery]'); if (!gallery) return;
    var picks = mappedPicks(), color = picks.opts['Caliper Color'], dia = picks.opts['Disc Diameter'], activeDisc = picks.opts['Disc Type'];
    var seen = {}, thumbs = [];
    matrix.forEach(function (v) {
      if (v.opts['Caliper Color'] !== color || !v.image) return;
      if (dia && v.opts['Disc Diameter'] !== dia) return;
      var dt = v.opts['Disc Type']; if (seen[dt]) return; seen[dt] = true;
      thumbs.push({ disc: dt, image: v.image });
    });
    // Fallback: color didn't match — show one thumb per disc type from any color.
    if (!thumbs.length) {
      seen = {};
      matrix.forEach(function (v) {
        if (!v.image) return;
        if (dia && v.opts['Disc Diameter'] !== dia) return;
        var dt = v.opts['Disc Type']; if (seen[dt]) return; seen[dt] = true;
        thumbs.push({ disc: dt, image: v.image });
      });
    }
    // Builder umbrellas no longer render a server-side strip (fyre-template-product.liquid gates it on
    // builder_collection_id), so this finds nothing there. It is kept for the page that DOES still emit one:
    // an umbrella whose builder collection resolves no id renders the static rail AND no resolver div, and a
    // future caller reaching this function on such a page must not end up with two strips.
    var staticStrip = gallery.querySelector('.pdp-gallery__thumbs--vertical:not([data-fyre-thumbs])');
    if (staticStrip && staticStrip.parentNode) staticStrip.parentNode.removeChild(staticStrip);
    var strip = gallery.querySelector('[data-fyre-thumbs]');
    // a single thumbnail has nothing to switch between — hide the strip rather than show a lone, useless thumb
    if (thumbs.length < 2) { if (strip && strip.parentNode) strip.parentNode.removeChild(strip); return; }
    if (!strip) {
      strip = document.createElement('div');
      strip.className = 'pdp-gallery__thumbs pdp-gallery__thumbs--vertical';
      strip.setAttribute('data-fyre-thumbs', '');
      var mainWrap = gallery.querySelector('.pdp-gallery__main-wrap');
      if (mainWrap) { gallery.insertBefore(strip, mainWrap); } else { gallery.appendChild(strip); }
    }
    strip.innerHTML = thumbs.map(function (t) {
      return '<button type="button" class="pdp-gallery__thumb' + (t.disc === activeDisc ? ' pdp-gallery__thumb--active' : '') +
        '" data-fyre-thumb-disc="' + t.disc + '" aria-label="' + t.disc + '"><img src="' + sizedUrl(t.image, 130) +
        '" alt="' + t.disc + '" width="65" height="65" loading="lazy"></button>';
    }).join('');
    Array.prototype.slice.call(strip.querySelectorAll('[data-fyre-thumb-disc]')).forEach(function (b) {
      b.addEventListener('click', function () { selectDisc(b.getAttribute('data-fyre-thumb-disc')); });
    });
  }

  // Resolved-variant VIEW gallery — Main + PDP images 2/3/4 (content.pdp_image_*), pulled from the backing
  // variant via MATRIX_QUERY (v.images). Display-only: clicking a thumb swaps the main image with a plain
  // setGalleryImage (NO re-resolve — unlike the disc-type switcher in populateThumbnails).
  //
  // WHY it writes into the disc-switcher's own strip: the PDP has exactly ONE thumbnail gallery — the vertical
  // strip left of the main image. A second strip on the right would put two visible galleries either side of the
  // image. The variant gallery OVERTAKES the left strip instead: same container, same vertical layout, view
  // thumbs replacing disc thumbs while a resolved variant has images to show. populateViewThumbs runs AFTER
  // populateThumbnails on the resolve path (see the resolve success block), so the takeover is just render order.
  function populateViewThumbs(v) {
    var gallery = document.querySelector('[data-pdp-gallery]'); if (!gallery) return;
    var strip = gallery.querySelector('[data-fyre-thumbs]');
    var imgs = (v && v.images) || [];
    // Nothing to switch between (cleared, or a lone image) — hand the one strip back to the disc-type switcher
    // rather than leaving the gallery bare. This is the reverse of the takeover and MUST stay in step with it:
    // failClosed() calls populateViewThumbs(null), and the shopper should get the disc thumbs back, not nothing.
    if (imgs.length < 2) {
      // NEVER delete a strip this function does not own. failClosed() calls populateViewThumbs(null) on EVERY
      // unresolved exit, including exits where the strip currently holds DISC thumbs — blindly removing it there
      // would destroy a perfectly good disc gallery and leave the shopper with nothing.
      var ownsStrip = viewThumbsActive || !!(strip && strip.querySelector('[data-fyre-view-url]'));
      viewThumbsActive = false; // release BEFORE any populateThumbnails call, or it guards itself out
      if (!ownsStrip) return;   // disc thumbs (or no strip) — not ours to touch
      // Prefer repainting IN PLACE over removing: the repaint reuses the same container, so the gallery never
      // blinks empty. representativeMatrix is the no-vehicle gallery's own matrix and covers the case where
      // removing the vehicle or a non-fitting pick has already CLEARED activeBacking.
      //
      // Hand back to setInitialGallery, NOT straight to populateThumbnails: the unresolved gallery is now the
      // VIEW gallery (see setInitialGallery), so an unresolved exit must land on the same photos a cold load
      // shows — not on disc thumbs the shopper never asked for. It also keeps the strip DISPLAY-ONLY on this
      // path. Disc thumbs are interactive (selectDisc -> radio change -> applyResolution), and repainting them
      // while a matrix is cached but the buyer-price overlay has not landed lets a B2B shopper resolve and add
      // to cart at RETAIL pricing. setInitialGallery still falls back to disc thumbs when the variant has
      // fewer than 2 images, so that hazard is narrowed to imageless backings rather than every exit.
      // Terminates: setInitialGallery only re-enters this function on the >=2-image path, which paints.
      var fallbackMatrix = (activeBacking && matrixCache[activeBacking.id]) || representativeMatrix;
      if (fallbackMatrix) { setInitialGallery(fallbackMatrix); return; }
      // No matrix anywhere (first load, or every representative load rejected). Deliberately does NOT retain the
      // strip: what it holds is the PRIOR vehicle's view thumbs, still clickable, for a config the shopper can no
      // longer buy — the mixed-identity defect. An empty gallery is correct; stale-but-populated is not.
      if (strip && strip.parentNode) strip.parentNode.removeChild(strip);
      return;
    }
    if (!strip) {
      strip = document.createElement('div');
      strip.className = 'pdp-gallery__thumbs pdp-gallery__thumbs--vertical';
      strip.setAttribute('data-fyre-thumbs', '');
      var mainWrap = gallery.querySelector('.pdp-gallery__main-wrap');
      if (mainWrap) { gallery.insertBefore(strip, mainWrap); } else { gallery.appendChild(strip); }
    }
    viewThumbsActive = true; // the view gallery now owns the left strip; populateThumbnails stands down
    strip.innerHTML = imgs.map(function (u, i) {
      return '<button type="button" class="pdp-gallery__thumb' + (i === 0 ? ' pdp-gallery__thumb--active' : '') +
        '" data-fyre-view-url="' + u + '" aria-label="View ' + (i + 1) + '"><img src="' + sizedUrl(u, 130) +
        '" alt="View ' + (i + 1) + '" width="65" height="65" loading="lazy"></button>';
    }).join('');
    Array.prototype.slice.call(strip.querySelectorAll('[data-fyre-view-url]')).forEach(function (b) {
      b.addEventListener('click', function () {
        setGalleryImage(b.getAttribute('data-fyre-view-url'));
        Array.prototype.slice.call(strip.querySelectorAll('.pdp-gallery__thumb')).forEach(function (x) { x.classList.remove('pdp-gallery__thumb--active'); });
        b.classList.add('pdp-gallery__thumb--active');
      });
    });
  }

  // Drop the view gallery WITHOUT painting a replacement. Used the moment a new vehicle starts resolving: until the
  // new backing's matrix lands we do not yet know what to show, and the thumbs still on screen belong to the PREVIOUS
  // vehicle. They stay clickable through the fetch, so a shopper can swap the main image to a config they can no
  // longer buy — the mixed-identity class.
  //
  // WHY THIS IS NOT populateViewThumbs(null): that hands the strip back to the disc-type switcher, whose thumbs are
  // INTERACTIVE (a click re-runs the configurator), and repainting interactive controls mid-flight is unsafe.
  // Removing the strip paints nothing and can trigger nothing. An empty gallery for the length of one fetch is
  // honest; stale-but-clickable is not — the same call the no-matrix branch of populateViewThumbs already makes.
  //
  // Only ever removes a strip the VIEW gallery owns. A strip holding disc thumbs is left alone: the shopper can still
  // switch disc type while the new vehicle resolves, and that strip is not tied to the outgoing vehicle.
  function clearViewThumbs() {
    var gallery = document.querySelector('[data-pdp-gallery]'); if (!gallery) return;
    var strip = gallery.querySelector('[data-fyre-thumbs]');
    var ownsStrip = viewThumbsActive || !!(strip && strip.querySelector('[data-fyre-view-url]'));
    viewThumbsActive = false;
    if (!ownsStrip || !strip || !strip.parentNode) return;
    strip.parentNode.removeChild(strip);
  }

  // signal the resolved backing SKU to fyre-pdp.js's FyreFitmentNote (its modules are IIFE-private → not directly
  // callable). The note matches installation notes on (vehicle × SKU); on umbrellas it otherwise only ever sees the
  // null umbrella SKU, so a note could never fire on a builder kit. Cleared (null) on every fail-closed.
  function notifyResolved(sku) { try { window.dispatchEvent(new CustomEvent('fyre:resolved', { detail: { sku: sku || null } })); } catch (e) {} }

  // fail-closed also hides the companion ("Complete Your Brake System") card — no resolved SKU → no paired umbrella to show.
  // WHY the view-thumb clear lives HERE and not at each call site: the strip is built only on a successful resolve, but
  // every unresolved exit (no fit, no variant, no diameter, matrix reject) routes through failClosed. Leaving it up let the
  // PRIOR vehicle's thumbs stay clickable while the resolver reports "does not fit" — clicking one would swap the main image
  // back to a config the shopper can no longer buy (the mixed-identity class). One choke point covers future fail
  // paths too. populateViewThumbs(null) hits its own "<2 images → remove the strip" branch. The resolved-OOS branch
  // re-populates AFTER failClosed (same ordering as renderCompanion), so OOS keeps its thumbs by design.
  function failClosed(msg) { setVariantId(''); clearLineItemProps(); delete root.dataset.resolvedSku; delete root.dataset.resolvedId; setAtc(true, msg); notifyResolved(null); hideCompanion(); populateViewThumbs(null); }

  function candidates(vh) { return (!graph || !vh) ? [] : graph.filter(function (b) { return b.vehicles[vh]; }); }

  function driveFitmentBar() {
    var bar = document.querySelector('[data-pdp-fitment]'); if (!bar || !graph) return;
    var vh = ambientVehicleHandle();
    if (!vh) { bar.classList.remove('pdp-fitment--confirmed', 'pdp-fitment--no-fit'); return; }
    var fits = graph.some(function (b) { return b.vehicles[vh]; });
    bar.classList.add('pdp-fitment--has-vehicle');
    bar.classList.toggle('pdp-fitment--confirmed', fits);
    bar.classList.toggle('pdp-fitment--no-fit', !fits);
  }

  // enable only the picker option-values the matched backing actually offers (dynamic; disable impossible)
  function applyOptionAvailability(matrix) {
    if (!matrix) { optionRadios().forEach(function (r) { r.disabled = false; r.closest('label') && r.closest('label').classList.remove('is-unavailable'); }); return; }
    // available backing values per backing option name
    var avail = {};
    matrix.forEach(function (v) { Object.keys(v.opts).forEach(function (bn) { (avail[bn] = avail[bn] || {})[v.opts[bn]] = true; }); });
    var byGroup = {};
    optionRadios().forEach(function (r) {
      var m = /options\[(.+?)\]/.exec(r.name); if (!m) return;
      var backingName = NAME_TO_BACKING[m[1].toLowerCase()] || m[1];
      if (backingName === 'Disc Diameter') return; // governed by ensureDiameterPickFits (#1)
      var backingValue = r.value;
      // Custom is a paid ADD-ON (custom caliper paint, +$400), not a stock configuration. Its backing row is the
      // blank-SKU placeholder variant, so an availability sweep keyed on backing rows finds nothing and marks it
      // unavailable — which is how it came to render greyed out pre-vehicle. It is orderable wherever the option
      // offers it, so it is exempt from this sweep entirely. Still pushed to byGroup so the auto-correct below can
      // land on it when every stock value in the group is genuinely gone.
      if (backingValue === 'Custom') {
        r.disabled = false;
        var customLabel = r.closest('label'); if (customLabel) customLabel.classList.remove('is-unavailable');
        if (!byGroup[r.name]) byGroup[r.name] = [];
        byGroup[r.name].push(r);
        return;
      }
      var ok = !avail[backingName] || !!avail[backingName][backingValue];
      r.disabled = !ok;
      var label = r.closest('label'); if (label) label.classList.toggle('is-unavailable', !ok);
      if (!byGroup[r.name]) byGroup[r.name] = [];
      byGroup[r.name].push(r);
    });
    // Auto-correct: if the checked radio in a group is now unavailable, pick the first available one.
    Object.keys(byGroup).forEach(function (name) {
      var group = byGroup[name];
      var checked = group.filter(function (r) { return r.checked; })[0];
      if (checked && checked.disabled) {
        var first = group.filter(function (r) { return !r.disabled; })[0];
        if (first) { first.checked = true; first.dispatchEvent(new Event('change', { bubbles: true })); }
      }
    });
  }

  // DIAMETER is the backing/product axis: for the current vehicle, enable only diameters that have a fitting backing,
  // and auto-correct the checked diameter if it doesn't fit. Returns the (possibly corrected) normalized pick. (#1)
  function ensureDiameterPickFits(fitDias) {
    var diaRadios = optionRadios().filter(isDiameterRadio);
    if (!diaRadios.length) return normDia((mappedPicks().opts['Disc Diameter']) || ''); // single-diameter family (no diameter option)
    var haveDiaData = Object.keys(fitDias).length > 0; // if backings carry no diameter metafield, don't lock out every diameter
    var checked = null, firstFit = null;
    diaRadios.forEach(function (r) {
      var fits = !haveDiaData || !!fitDias[normDia(r.value)];
      r.disabled = !fits;
      var label = r.closest('label'); if (label) label.classList.toggle('is-unavailable', !fits);
      if (fits && !firstFit) firstFit = r;
      if (r.checked) checked = r;
    });
    if ((!checked || checked.disabled) && firstFit) { firstFit.checked = true; } // correct in place; caller recomputes the pick (no dispatch → no recursion)
    return normDia(mappedPicks().opts['Disc Diameter'] || '');
  }

  function isCustomBackingColor(value) {
    var h = String(value || '').toLowerCase().replace(/\s+/g, '-');
    return h === 'custom' || h === 'custom-color';
  }
  // The Custom paint ADD-ON toggle (sections/fyre-template-product.liquid). It is not an option radio, so it never
  // enters resolution; it only decides which colour sibling of the resolved config goes on the cart line.
  function customPaintActive() {
    var t = document.querySelector('[data-custom-color-toggle]');
    return !!(t && t.checked && !t.disabled);
  }
  // Custom paint is sold on PAINTED calipers only. Liquid gates the umbrella on its own
  // specs.caliper_finish; this re-checks against the RESOLVED backing's finish and withdraws the tile — and any
  // Custom selection — when the backing is Nickel Plated / Hard Anodized. No backing yet -> leave the server state.
  function applyCustomPaintGate() {
    var tile = document.querySelector('[data-custom-color-swatch]'); if (!tile) return;
    var specs = backingSpecsCache[activeBacking && activeBacking.id]; if (!specs) return;
    var painted = specs.caliper_finish === 'Painted';
    tile.hidden = !painted;
    var toggle = tile.querySelector('[data-custom-color-toggle]');
    if (!painted && toggle && toggle.checked) {
      toggle.checked = false;
      toggle.dispatchEvent(new Event('change', { bubbles: true })); // fyre-pdp.js closes the panel + reprices
    }
  }

  // Custom Color backing variants carry a blank SKU — a pricing/UI placeholder, not a real, fulfillable stock
  // item (the customer's actual requested color/logo/paint-code rides the separate Custom Caliper Paint addon
  // line, harvested by fyre-pdp.js's FyreATC.submit from the same custom-color panel this PDP shares with
  // standalone products). The cart line must carry a real color variant instead: whichever sibling (same Disc
  // Type/Diameter/etc — every OTHER option identical) has the most on-hand inventory. Returns null when Custom
  // is resolved but no in-stock real sibling exists, so the caller fails closed instead of falling back to the
  // no-SKU variant.
  function bestRealColorVariant(matrix, v) {
    if (!matrix || !v) return null;
    var best = null;
    matrix.forEach(function (m) {
      if (isCustomBackingColor(m.opts['Caliper Color']) || !m.available) return;
      var matches = Object.keys(v.opts).every(function (bn) { return bn === 'Caliper Color' || m.opts[bn] === v.opts[bn]; });
      if (!matches) return;
      var qty = typeof m.qty === 'number' ? m.qty : 0;
      if (!best || qty > best.qty) best = { variant: m, qty: qty };
    });
    return best ? best.variant : null;
  }

  function resolveFromMatrix() {
    if (!activeBacking) return null;
    var matrix = matrixCache[activeBacking.id]; if (!matrix) return null;
    var picks = mappedPicks();
    // backing is already diameter-correct (selected via the graph) → match only Color×DiscType. Skipping Diameter
    // also tolerates backings whose variants don't carry a Disc Diameter option at all (#1/C2).
    var hit = matrix.filter(function (v) {
      return Object.keys(picks.opts).every(function (bn) { return bn === 'Disc Diameter' || v.opts[bn] === picks.opts[bn]; });
    });
    return { variant: hit.length === 1 ? hit[0] : null, count: hit.length };
  }

  // ── Replacement Parts panel ── inject the resolved backing-variant's parts into the server-rendered
  // [data-pdp-replacement-parts] host (which otherwise shows the dead umbrella variant). Mirrors the Liquid card
  // markup so the existing CSS applies. Text rows for SKU strings; <a> cards where a product_reference exists.
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  function replacementsHost() { return document.querySelector('[data-pdp-replacement-parts]'); }
  function partCardLinked(card, hidePrice) {
    var img = card.image
      ? '<div class="pdp-info__part-img"><img class="pdp-info__part-image" src="' + sizedUrl(card.image, 160) + '" alt="' + esc(card.title) + '" loading="lazy"></div>'
      : '<div class="pdp-info__part-img-placeholder"></div>';
    var price = card.price && !hidePrice ? '<p class="pdp-info__part-price">' + money(card.price) + '</p>' : '';
    return '<a href="' + esc(card.url || '#') + '" class="pdp-info__part-card pdp-info__part-card--linked">' + img + '<p class="pdp-info__part-name">' + esc(card.title) + '</p>' + price + '</a>';
  }
  function partCardText(label, sku) {
    return '<div class="pdp-info__part-card"><div class="pdp-info__part-img-placeholder"></div><p class="pdp-info__part-name">' + label + '<br><small>' + esc(sku) + '</small></p></div>';
  }
  function partsRow(iconClass, label, cards) {
    return '<div class="pdp-info__parts-row"><div class="pdp-info__parts-label-col"><span class="pdp-info__parts-label-icon" aria-hidden="true"><i class="' + iconClass + '"></i></span><span class="pdp-info__parts-label-text">' + label + '</span></div><div class="pdp-info__parts-grid">' + cards + '</div></div>';
  }
  function setReplacementsNote(msg) { var h = replacementsHost(); if (h) h.innerHTML = '<p class="pdp-info__empty-note">' + esc(msg) + '</p>'; }
  function renderReplacements(v) {
    var host = replacementsHost(); if (!host) return;
    var r = (v && v.repl) || {}, disc = discProductCache[activeBacking && activeBacking.id] || null, rows = '';
    if (disc) rows += partsRow('fa-regular fa-record-vinyl', 'Discs', partCardLinked(disc));
    else if (r.disc_lh || r.disc_rh) {
      var dc = ''; if (r.disc_lh) dc += partCardText('Disc LH', r.disc_lh); if (r.disc_rh) dc += partCardText('Disc RH', r.disc_rh);
      rows += partsRow('fa-regular fa-record-vinyl', 'Discs', dc);
    }
    if (r.pad_product || r.pad_set) {
      // CATALOG-MODE: pad price hidden while the store is catalog-only. Restore by dropping the `true`.
      var pc = r.pad_product ? partCardLinked(r.pad_product, true) : partCardText('Default Pad Set', r.pad_set);
      pc += '<a href="/collections/brake-pads" class="pdp-info__part-shopall-card">SHOP ALL<br>BRAKE PADS</a>';
      rows += partsRow('fa-regular fa-layer-group', 'Brake Pads', pc);
    }
    var ac = '';
    if (r.disc_hw) ac += partCardText('Hardware Kit', r.disc_hw);
    if (r.bracket) ac += partCardText('Bracket', r.bracket);
    if (r.brake_line) ac += partCardText('Brake Line', r.brake_line);
    if (r.disc_bell) ac += partCardText('Disc Bell', r.disc_bell);
    if (ac) rows += partsRow('fa-regular fa-toolbox', 'Accessories', ac);
    host.innerHTML = rows || '<p class="pdp-info__empty-note">Replacement parts data not available for this configuration.</p>';
  }

  // ── Vehicle Compatibility panel ── the umbrella's own variants carry no fitment, so the server-rendered
  // table is empty. Group names already encode "Make Model … Year" (mirrors the Liquid parse). Rendering is
  // driven from setActiveBacking() so the panel is populated as soon as a backing is in hand.
  function vehicleCompatHost() { return document.querySelector('[data-pdp-vehicle-compat]'); }
  // Collapse a model's individual years into "1990–1995, 2001" ranges (numeric only; junk years dropped).
  function compatYearRange(yearObj) {
    var nums = Object.keys(yearObj).map(function (y) { return parseInt(y, 10); }).filter(function (n) { return !isNaN(n); });
    nums = nums.filter(function (n, i) { return nums.indexOf(n) === i; }).sort(function (a, b) { return a - b; });
    var out = [], s = null, p = null;
    nums.forEach(function (n) { if (s === null) { s = n; p = n; return; } if (n === p + 1) { p = n; return; } out.push(s === p ? '' + s : s + '–' + p); s = n; p = n; });
    if (s !== null) out.push(s === p ? '' + s : s + '–' + p);
    return out.join(', ');
  }
  // Build the grouped-by-make UI from the umbrella's OWN fitment.groups → vehicles (make/model/year/chassis).
  // Real models — NOT the opaque "(N models)" labels — so a shopper actually finds their car. Chassis code shown
  // (1–3 distinct) to disambiguate generations; years collapsed to ranges. data-search carries "make model" for filtering.
  function buildCompatHTML(groups, truncated) {
    var tree = {}; // make -> modelKey -> { model, sub, years:{}, chassis:{} }
    groups.forEach(function (g) {
      var vs = (g && g.v && g.v.references && g.v.references.nodes) || [];
      vs.forEach(function (m) {
        if (!m) return; // skip dangling/null metaobject refs — a deleted group→vehicle ref must not throw the whole panel
        var mk = (m.mk && m.mk.value) || '', md = (m.md && m.md.value) || '', yr = (m.yr && m.yr.value) || '',
            ch = (m.ch && m.ch.value) || '', sm = (m.sm && m.sm.value) || '';
        if (!mk || !md) return;
        // WHY: submodel is a LOAD-BEARING fitment qualifier (PCCB / Carbon-Ceramic / xDrive / M-Sport). Key rows on
        // model+submodel so a qualified fitment never collapses into — and overstates — the base-model row.
        var key = sm ? md + '|' + sm : md;
        if (!tree[mk]) tree[mk] = {};
        if (!tree[mk][key]) tree[mk][key] = { model: md, sub: sm, years: {}, chassis: {} };
        if (yr) tree[mk][key].years[yr] = 1;
        if (ch) tree[mk][key].chassis[ch] = 1;
      });
    });
    var makes = Object.keys(tree).sort();
    if (!makes.length) return '<p class="pdp-info__empty-note">No vehicle compatibility data available for this product.</p>';
    // The panel narrows to one backing, which is usually a single make and a short list — so the make accordion and
    // the type-ahead are chrome for a problem that no longer exists. Both appear only when they earn their place:
    // the accordion when there is more than one make (fitment groups DO span makes — BMW Z4 / GR Supra, XLR /
    // Corvette, G35 / 350Z), the filter when the list is long enough to be worth searching.
    // withMake prefixes the label when the rows are flattened across makes, so "BRZ" still reads as "Subaru BRZ".
    function rowsHTML(mk, withMake) {
      return Object.keys(tree[mk]).sort().map(function (key) {
        var e = tree[mk][key], ch = Object.keys(e.chassis);
        var label = e.model + (e.sub ? ' ' + e.sub : '') + (ch.length && ch.length <= 3 ? ' (' + ch.sort().join(', ') + ')' : '');
        var yrs = compatYearRange(e.years);
        return '<li class="pdp-compat__row" data-compat-row data-search="' + esc((mk + ' ' + label).toLowerCase()) + '">' +
          '<span class="pdp-compat__model">' + esc(withMake ? mk + ' ' + label : label) + '</span>' +
          (yrs ? '<span class="pdp-compat__years">' + esc(yrs) + '</span>' : '') + '</li>';
      }).join('');
    }
    var rowTotal = makes.reduce(function (n, mk) { return n + Object.keys(tree[mk]).length; }, 0);
    // One threshold drives both affordances, because both answer the same question — is this list long enough to
    // need navigating? A narrowed backing is usually a handful of rows, where an accordion costs a click to reveal
    // the only content and a type-ahead searches a list you can already read. Groups DO span makes (Scion FR-S /
    // Subaru BRZ / Toyota 86 is one backing), so the make grouping stays for long multi-make lists and the make
    // is folded into the row label when the list is flattened.
    var NAV_MIN = 15;
    var flat = makes.length === 1 || rowTotal < NAV_MIN;
    var html = '<div class="pdp-compat" data-compat-ui>';
    if (rowTotal >= NAV_MIN) {
      html += '<input type="text" class="pdp-compat__filter" data-compat-filter placeholder="Start typing your make or model…" aria-label="Filter vehicle compatibility">';
    }
    html += '<div class="pdp-compat__makes">';
    if (flat) {
      html += '<ul class="pdp-compat__list pdp-compat__list--flat">' +
        makes.map(function (mk) { return rowsHTML(mk, makes.length > 1); }).join('') + '</ul>';
    } else {
      makes.forEach(function (mk) {
        html += '<details class="pdp-compat__make"><summary class="pdp-compat__make-hdr"><span class="pdp-compat__make-name">' + esc(mk) +
          '</span><span class="pdp-compat__make-count">' + Object.keys(tree[mk]).length + '</span></summary>' +
          '<ul class="pdp-compat__list">' + rowsHTML(mk, false) + '</ul></details>';
      });
    }
    html += '</div>';
    // WHY: never present a partial list as complete — a fitting car silently missing reads as a false "no match".
    if (truncated) html += '<p class="pdp-compat__trunc">Some vehicles may not be listed here — use the fitment selector above to confirm yours.</p>';
    html += '<p class="pdp-compat__noresult" data-compat-noresult hidden>No match — try another model, or use the selector above.</p></div>';
    return html;
  }
  // The panel shows the compatibility of the RESOLVED backing, not the umbrella union. An umbrella spans every
  // diameter and caliper in the family, so its union lists cars that the SKU in the cart does not fit — which reads
  // as a compatibility promise the product cannot keep. Until a backing resolves there is no honest list to show,
  // so the panel carries a prompt instead of data.
  //
  // This reverses the 2026-07-16 position ("always the union; the fitment bar already confirms the shopper's
  // vehicle"). The client asked for the narrowing directly (2026-08-18), because the union was being read as the
  // SKU's own fitment.
  //
  // One fetch, reused: the umbrella's fitment.groups → vehicles is pulled ONCE and kept as raw groups. Narrowing is
  // a filter over those groups by the backing's own group names (the resolution graph carries them per backing), so
  // changing diameter re-filters in memory and never re-queries.
  var compatGroups = null, compatStarted = false, compatPaintedFor = undefined;
  // Filter the umbrella's groups down to the ones this backing actually carries, then paint. The resolution graph
  // already holds each backing's group IDs, and the compat fetch reads the same id, so the two join with no extra
  // request. The key is the metaobject ID and must stay so: group NAMES are generated summaries and collide across
  // distinct groups ("BMW (31 models) 2012–2021" names two groups with different vehicle sets), so a name join
  // silently unions them and the panel stops changing when the shopper picks a different diameter.
  function compatGroupsFor(backingId) {
    var all = (compatGroups && compatGroups.groups) || [];
    var b = null;
    for (var i = 0; i < (graph || []).length; i++) { if (graph[i].id === backingId) { b = graph[i]; break; } }
    var ids = b && b.groupIds;
    if (!ids || !ids.length) return null; // unknown backing / no groups recorded -> caller decides
    var want = {};
    ids.forEach(function (n) { want[n] = true; });
    return all.filter(function (g) { return g && want[g.id]; });
  }
  function paintCompat(host, backingId) {
    compatPaintedFor = backingId;
    var picked = compatGroupsFor(backingId);
    if (!picked || !picked.length) {
      // The backing resolved from a vehicle, so it HAS fitment — an empty narrow means the group names drifted
      // between the graph and the fitment metaobjects. Say nothing rather than fall back to the umbrella union,
      // which is the overstatement this narrowing exists to stop.
      console.warn('[fyre-resolve] vehicle-compat: no groups matched backing ' + backingId + ' — showing empty state');
      host.innerHTML = '<p class="pdp-info__empty-note">Vehicle compatibility for this configuration is unavailable.</p>';
      return;
    }
    // Truncation is scoped to the groups actually PAINTED. compatGroups.truncated is umbrella-wide, so carrying it
    // onto a narrowed backing whose own groups are complete printed "Some vehicles may not be listed here" over a
    // list that is in fact exhaustive — a false hedge, which costs trust the same way a false claim does.
    var picketTruncated = !!compatGroups.listTruncated;
    for (var pi = 0; !picketTruncated && pi < picked.length; pi++) {
      var pvr = picked[pi] && picked[pi].v && picked[pi].v.references;
      if (pvr && pvr.pageInfo && pvr.pageInfo.hasNextPage) picketTruncated = true;
    }
    host.innerHTML = buildCompatHTML(picked, picketTruncated);
    wireCompatFilter(host);
  }
  function renderVehicleCompat() {
    var host = vehicleCompatHost(); if (!host) return;
    var backingId = activeBacking ? activeBacking.id : null;
    // Re-paint only when the RESOLVED BACKING changes. Re-running innerHTML on every setActiveBacking would wipe
    // the shopper's typed filter text and collapse open <details> mid-interaction, so an unchanged backing is a
    // no-op even though the panel is no longer static.
    if (compatPaintedFor === backingId && host.querySelector('[data-compat-ui], [data-compat-prompt]')) return;
    // No backing yet (no vehicle, no fitting backing, or no diameter picked) -> prompt, never a list.
    if (!backingId) {
      compatPaintedFor = backingId;
      host.innerHTML = '<p class="pdp-info__empty-note" data-compat-prompt>' +
        esc(ambientVehicleHandle() ? 'This system does not fit the selected vehicle.' : 'Enter your vehicle details above to see compatibility.') + '</p>';
      return;
    }
    if (compatGroups) { paintCompat(host, backingId); return; }
    host.innerHTML = '<p class="pdp-info__empty-note">Loading vehicle compatibility…</p>';
    if (compatStarted) return; // fetch already scheduled/running — its .then() paints the (single) host
    var pid = host.dataset.fitmentProductGid; if (!pid) return;
    compatStarted = true;
    var Q = 'query($id: ID!) { product(id: $id) { mf: metafield(namespace: "fitment", key: "groups") {' +
      ' references(first: 250) { pageInfo { hasNextPage } nodes { ... on Metaobject {' +
      ' id' +
      ' v: field(key: "vehicles") { references(first: 250) { pageInfo { hasNextPage } nodes { ... on Metaobject {' +
      ' mk: field(key: "make") { value } md: field(key: "model") { value } yr: field(key: "year") { value } ch: field(key: "chassis_code") { value } sm: field(key: "submodel") { value }' +
      ' } } } } } } } } } }';
    // Retry on the transient Storefront throttle (leaky-bucket) — back off so the shared rate bucket refills.
    function attempt(retriesLeft, delay) {
      return sf(Q, { id: pid }).catch(function (e) {
        if (retriesLeft <= 0) throw e;
        return new Promise(function (r) { setTimeout(r, delay); }).then(function () { return attempt(retriesLeft - 1, delay * 2); });
      });
    }
    // DEFER past the boot burst: umbrella init fires the heavy GRAPH_QUERY (all backings → groups → vehicles),
    // which drains the SHARED Storefront rate bucket — a compat fetch fired synchronously alongside it throttles
    // and shows "unavailable". Waiting for idle lets the bucket refill; retry is the backstop.
    defer(function () {
      attempt(3, 700).then(function (d) {
        var refs = d && d.product && d.product.mf && d.product.mf.references;
        var groups = (refs && refs.nodes) || [];
        // TRUNCATION, stored in two parts because the panel is now narrowed. A short groups LIST means the backing
        // may be missing a group entirely, so it applies to every paint; a truncated group's VEHICLES connection
        // only matters if that group is among the ones painted, which paintCompat decides.
        compatGroups = { groups: groups, listTruncated: !!(refs && refs.pageInfo && refs.pageInfo.hasNextPage) };
        // The shopper can clear or change the vehicle while this request is in flight. Re-enter through
        // renderVehicleCompat rather than painting directly: painting a null backing reports "configuration
        // unavailable" for what is actually "no vehicle chosen yet", which reads as a fault in the product.
        compatPaintedFor = undefined;
        renderVehicleCompat();
      }).catch(function (e) {
        console.warn('[fyre-resolve] vehicle-compat', e);
        // Unlatch so a later re-render (vehicle/diameter change) RETRIES — otherwise compatStarted stays
        // true and the panel is stuck on "Loading…" forever after one transient failure.
        compatStarted = false;
        var h = vehicleCompatHost(); if (!h) return;
        // Same in-flight guard: with no backing selected the honest state is the prompt, not a failure notice.
        if (!activeBacking) { compatPaintedFor = undefined; return renderVehicleCompat(); }
        h.innerHTML = '<p class="pdp-info__empty-note">Vehicle compatibility is temporarily unavailable.</p>';
      });
    });
  }
  // Live filter: hide non-matching rows/makes; auto-open makes with a hit while typing, re-collapse when cleared.
  // Matching a make name (e.g. "bmw") reveals all its rows; otherwise match the full group label per row.
  // Two structures reach this: the make-grouped <details> tree, and the flat <ul>. The flat branch is NOT optional
  // to handle — the filter renders at 15+ rows and single-make lists flatten, so the long single-make list is the
  // most common filtered case. Walking only `.pdp-compat__make` there matches nothing, leaves every row visible and
  // still raises "No match" on every keystroke.
  function wireCompatFilter(host) {
    var input = host.querySelector('[data-compat-filter]'); if (!input) return;
    var makes = Array.prototype.slice.call(host.querySelectorAll('.pdp-compat__make'));
    // A flat row's data-search already carries "make label", so a make query matches without the group header.
    var flatRows = makes.length ? [] : Array.prototype.slice.call(host.querySelectorAll('[data-compat-row]'));
    var noRes = host.querySelector('[data-compat-noresult]');
    input.addEventListener('input', function () {
      var q = input.value.trim().toLowerCase(), anyVisible = false;
      makes.forEach(function (det) {
        var mkMatch = q && det.querySelector('.pdp-compat__make-name').textContent.toLowerCase().indexOf(q) !== -1;
        var any = false;
        Array.prototype.slice.call(det.querySelectorAll('[data-compat-row]')).forEach(function (li) {
          var m = !q || mkMatch || li.getAttribute('data-search').indexOf(q) !== -1;
          li.hidden = !m; if (m) any = true;
        });
        det.hidden = !any; det.open = q ? any : false; if (any) anyVisible = true;
      });
      flatRows.forEach(function (li) {
        var m = !q || li.getAttribute('data-search').indexOf(q) !== -1;
        li.hidden = !m; if (m) anyVisible = true;
      });
      if (noRes) noRes.hidden = !(q && !anyVisible);
    });
  }

  // ── Tech Specs (disc) on the matrix path ── swap the server-rendered disc-spec cells to the resolved variant's
  // EXACT values. Variant-grain fields (type/air_gap/annulus/vane) come from the variant; finish/mounting from the
  // variant with a backing-constant fallback (non-Racing backings carry them product-level); diameter/thickness/
  // material/construction are always backing-constant. Writes via textContent on [data-spec] cells ONLY — never
  // innerHTML — so the tooltip / jump-nav / drag-scroll listeners on those cells survive untouched (kills the whole
  // SRA re-init problem class). Pre-resolution the cells keep their server-rendered umbrella-union defaults.
  var MM_SUFFIX = { disc_diameter: 1, disc_thickness: 1, disc_air_gap: 1, disc_annulus: 1 }; // numeric-mm fields: display adds "mm"
  var specCells = null, specDefaults = null;
  function findSpecCells() {
    if (specCells && Object.keys(specCells).length) return specCells;
    specCells = {};
    Array.prototype.slice.call(document.querySelectorAll('[data-spec]')).forEach(function (td) { specCells[td.getAttribute('data-spec')] = td; });
    return specCells;
  }
  function snapshotSpecDefaults() { // capture server-rendered (umbrella union) text ONCE, before any render mutates a cell
    if (specDefaults) return;
    var cells = findSpecCells(); if (!Object.keys(cells).length) return; // cells not in DOM (non-umbrella / not yet rendered)
    specDefaults = {}; Object.keys(cells).forEach(function (k) { specDefaults[k] = cells[k].textContent; });
  }
  function mmSuffix(key, v) { return (MM_SUFFIX[key] && /^[0-9.]+$/.test(v)) ? v + 'mm' : v; } // bare numeric-mm -> add "mm"; leave union strings/text alone
  function restoreSpecDefaults() { // back to pre-resolution umbrella-union display (no vehicle / no fit / custom / ambiguous)
    renderSpecDrawings(null);
    snapshotSpecDefaults(); if (!specDefaults) return;
    var cells = findSpecCells(); Object.keys(cells).forEach(function (k) { if (specDefaults[k] != null) cells[k].textContent = specDefaults[k]; });
  }
  // Disc face -> line-drawing map, handed over from Liquid on the resolve host. Keyed on the Disc Type entry
  // NAME, because that is the shape a variant's specs.disc_type carries: plain text, not a reference. Parsed
  // once; an absent or malformed attribute yields an empty map, which leaves the old backing behaviour intact.
  var discDrawingMap = null;
  function discDrawings() {
    if (discDrawingMap) return discDrawingMap;
    var host = document.querySelector('[data-fyre-resolve]');
    var raw = host && host.getAttribute('data-disc-drawings');
    try { discDrawingMap = (raw && JSON.parse(raw)) || {}; } catch (e) { discDrawingMap = {}; }
    return discDrawingMap;
  }
  // Which construction column a product reads. "2-Piece CCM-R" is a third live value and is a two-piece disc,
  // so anything not explicitly 1-piece takes the 2-piece drawing rather than falling through to no drawing.
  function constructionKey(text) { return /^\s*1-?\s*piece/i.test(String(text || '')) ? 'p1' : 'p2'; }
  // The drawing for a face+construction. '' means the map knows this face and has NO drawing for that
  // construction; null means the face is unknown, i.e. no opinion — keep whatever the caller already had.
  function discDrawingFor(face, construction) {
    var entry = discDrawings()[String(face || '').trim()];
    if (!entry) return null;
    return entry[constructionKey(construction)] || '';
  }
  // Point each Technical Specs drawing at the resolved backing's own, or back at the server-rendered one when there
  // is no backing or the backing carries none (same fallback the spec cells use).
  function renderSpecDrawings(backing, variant) {
    ['caliper', 'disc'].forEach(function (kind) {
      var img = document.querySelector('[data-spec-drawing="' + kind + '"]');
      if (!img) return;
      if (!img.hasAttribute('data-default-src')) img.setAttribute('data-default-src', img.getAttribute('src') || '');
      var url = backing && backing[kind + '_drawing'];
      var next = url ? sizedUrl(url, 600) : img.getAttribute('data-default-src');
      // The disc drawing is a function of the resolved FACE, which the backing cannot express: one backing spans
      // several faces, so backing.disc_drawing is only ever right by accident — it is drilled on every live
      // product today. A face the map knows but has no drawing for shows Image Coming Soon, deliberately, rather
      // than a near-miss drawing borrowed from another face or the wrong construction.
      if (kind === 'disc' && variant && variant.specs) {
        var byFace = discDrawingFor(variant.specs.disc_type, backing && backing.disc_construction);
        if (byFace !== null) {
          next = byFace || placeholderUrl() || next;
          img.setAttribute('alt', byFace ? 'Disc drawing' : 'Disc drawing coming soon');
        }
      }
      if (img.getAttribute('src') !== next) img.setAttribute('src', next);
    });
  }
  function renderTechSpecs(v) {
    snapshotSpecDefaults(); // ensure defaults captured before we overwrite
    if (!v || !v.specs) return restoreSpecDefaults();
    var cells = findSpecCells(), backing = backingSpecsCache[activeBacking && activeBacking.id] || {};
    renderSpecDrawings(backing, v);
    Object.keys(cells).forEach(function (key) {
      var value = v.specs[key] || backing[key] || ''; // variant-grain first, then backing-constant
      if (value) cells[key].textContent = mmSuffix(key, value);
      else if (specDefaults && specDefaults[key] != null) cells[key].textContent = specDefaults[key]; // neither -> keep union default
    });
  }

  // ── Resolved-backing copy: What's Included, and the short description under the title ────────────────────────
  // Both are backing-constant client copy the umbrella cannot state. Its kit_includes is blank wherever its kits
  // disagree, and its short_description names whichever disc was built first — so in both cases the server-rendered
  // value is a placeholder and only the resolved backing knows the real text. The umbrella value is therefore
  // NEVER rendered: prompt before a backing resolves, the backing's own text after, and nothing at all if a
  // backing somehow carries none. There is no fall back to the umbrella copy, because a kit that lists no contents
  // must show no contents — borrowing the parent's list would assert inclusions this SKU does not have.
  function copyHosts() {
    return { kit: document.querySelector('[data-pdp-kit-includes]'), desc: document.querySelector('[data-pdp-short-desc]') };
  }
  // kit_includes is a newline list. Mirror the Liquid render (one <li> per non-blank line) and escape every line:
  // it is spreadsheet copy and must never reach innerHTML as markup.
  function kitListHTML(text) {
    var lines = String(text || '').split(/\r?\n/).map(function (l) { return l.trim(); }).filter(Boolean);
    return lines.map(function (l) { return '<li class="pdp-info__included-item">' + esc(l) + '</li>'; }).join('');
  }
  // Until a backing resolves, PROMPT — never the umbrella's own copy. Measured on production 2026-08-21: the
  // umbrella value is wrong for at least one of its backings on 52% of umbrellas (short_description) and 41%
  // (kit_includes), and the failure is not a harmless superset — the GT|M front umbrella lists Goodridge brake
  // lines that the Corvette C7 kit does not contain, so a shopper reads a false inclusion claim about the box.
  // Same rule the compat panel now follows: state nothing SKU-specific before a SKU exists.
  // The prompt states WHY there is nothing to show. "Select your vehicle above" to a shopper who already selected
  // one — and whose vehicle simply does not fit — reads as a broken page. setReplacementsNote already distinguishes
  // these two cases; these panels now match it.
  function showCopyPrompts() {
    var h = copyHosts(), noVehicle = !ambientVehicleHandle();
    var kitMsg = noVehicle ? 'Select your vehicle above to see what\'s included.' : 'This system does not fit the selected vehicle.';
    var descMsg = noVehicle ? 'Select your vehicle above to see the exact specification.' : 'This system does not fit the selected vehicle.';
    if (h.kit) h.kit.innerHTML = '<li class="pdp-info__included-item pdp-info__included-item--prompt">' + esc(kitMsg) + '</li>';
    if (h.desc) h.desc.innerHTML = '<p class="pdp-info__empty-note">' + esc(descMsg) + '</p>';
  }
  function renderResolvedCopy() {
    var b = backingSpecsCache[activeBacking && activeBacking.id];
    if (!b) return showCopyPrompts();
    var h = copyHosts();
    // Empty renders empty. Verified on production 2026-08-21: all 1,640 builder backings carry both fields, so this
    // branch is unreachable today — it exists so that a future backing missing its copy shows nothing rather than
    // inheriting a claim from its parent.
    if (h.kit) h.kit.innerHTML = kitListHTML(b.kit_includes);
    if (h.desc) h.desc.innerHTML = b.short_description ? esc(b.short_description).replace(/\r?\n/g, '<br>') : '';
  }

  // ── Companion ("Complete Your Brake System") ── navigate-to-paired-rear-umbrella. The companion is a product_reference
  // (specs.companion_system) written on the resolved backing VARIANT (variant-grain: caliper-color splits pair to
  // different rears), so it is vehicle- AND config-correct. Pulled live per variant in MATRIX_QUERY → matrix row .companion;
  // CONFIGURE navigates to the rear umbrella, which auto-resolves the vehicle from shared localStorage.
  function hideCompanion() { if (companionHost) companionHost.style.display = 'none'; }
  // empty/guidance state: show the section with a prompt (e.g. no vehicle yet), hide the card row. Toggled via inline
  // style.display (NOT the hidden attr): .pdp-complete/.pdp-complete__row set display:flex, and author CSS overrides the
  // [hidden] UA rule — so hidden alone does not hide them, and an empty card would leak pre-selection.
  // Mirrors the replacement-parts note pattern.
  function companionNote(msg) {
    if (!companionHost) return;
    var row = companionHost.querySelector('[data-companion-row]');
    var note = companionHost.querySelector('[data-companion-note]');
    if (row) row.style.display = 'none';
    if (note) { note.style.display = ''; note.textContent = msg; }
    companionHost.style.display = '';
  }
  function renderCompanion(v) {
    if (!companionHost) return;
    var c = v ? v.companion : null; // variant-grain: the RESOLVED variant's paired rear (matrix row .companion)
    if (!c || !c.handle) { hideCompanion(); return; } // resolved, but this config has no paired rear -> hide
    var row = companionHost.querySelector('[data-companion-row]');
    var note = companionHost.querySelector('[data-companion-note]');
    var img = companionHost.querySelector('[data-companion-img]');
    var name = companionHost.querySelector('[data-companion-name]');
    var cta = companionHost.querySelector('[data-companion-cta]');
    if (img) { if (c.image) { img.src = sizedUrl(c.image, 200); img.alt = c.title || ''; img.style.display = ''; } else { img.style.display = 'none'; } }
    if (name) { name.textContent = c.title || ''; name.setAttribute('href', '/products/' + c.handle); } // title links to the companion PDP (CTA opens the flyout; the name navigates)
    if (cta) cta.setAttribute('href', '/products/' + c.handle);
    if (note) note.style.display = 'none';
    if (row) row.style.display = '';
    companionHost.style.display = '';
  }

  function applyResolution() {
    atcHeldForPads = false; // re-decided below; reset so a superseded buyable-pending flag can't leak onto an OOS/fail resolve and let releaseAtcForPads wrongly enable ATC
    setVariantId('');
    clearLineItemProps();
    delete root.dataset.resolvedSku;
    // no fitting backing for the current vehicle -> re-assert the vehicle-level message (don't hang on CHECKING…)
    if (!activeBacking) {
      if (representativeMatrix) setInitialGallery(representativeMatrix);
      restoreSpecDefaults(); // pre-resolution: show umbrella-union defaults, never a stale exact spec
      var vh = ambientVehicleHandle(); setReplacementsNote(!vh ? 'Select your vehicle to see exact replacement parts.' : 'This system does not fit the selected vehicle.'); return failClosed(!vh ? 'SELECT VEHICLE FIRST' : 'DOES NOT FIT THIS VEHICLE');
    }
    if (!matrixCache[activeBacking.id]) { setAtc(true, 'CHECKING…'); return; } // matrix genuinely still loading
    populateThumbnails(matrixCache[activeBacking.id]);
    applyCustomPaintGate();
    var res = resolveFromMatrix();
    if (!res) { setAtc(true, 'CHECKING…'); return; }
    var v = res.variant;
    // What's Included and the description are BACKING-constant, not variant-grain, so they are already correct here
    // even though no variant resolved. Returning without this left both panels prompting "select your vehicle" at a
    // shopper who had selected one — and only on a cold cache, because setActiveBacking paints before the matrix
    // lands, so a warm second visit showed the copy and a first visit did not.
    if (!v) { renderResolvedCopy(); restoreSpecDefaults(); setReplacementsNote('Select all options to see exact replacement parts.'); return failClosed(res.count > 1 ? 'CHOOSE OPTIONS' : 'NOT AVAILABLE FOR THIS COMBO'); }
    renderReplacements(v); // real backing-variant parts (valid even if OOS); panel reflects the resolved config
    renderTechSpecs(v);    // exact per-variant disc specs (valid even if OOS) — the display fix
    renderResolvedCopy();  // What's Included + short description off the resolved backing (setActiveBacking fires
                           // before the matrix lands, so the cache is usually empty there — this is the real paint)
    // IDENTITY displays (SKU/price/gallery) reflect the resolved variant even when OOS — otherwise the OOS state
    // showed specs+parts for the new variant but price/SKU/image for the PRIOR one (mixed identity). Do NOT move
    // setVariantId here: [name=id] must stay blank for an unbuyable variant (failClosed clears it below).
    setSku(v.sku);
    if (v.price) setPrice(v.price);
    if (v.image) setGalleryImage(v.image); else setGalleryPlaceholder();
    populateViewThumbs(v); // resolved-variant view gallery (Main + PDP images 2-4). Display-only; runs for available AND OOS.
    // OOS is a resolved-but-unbuyable state: identity/specs/parts (above) reflect the variant, and the paired-rear
    // companion should too — read-only navigation, no buyability coupling. failClosed disables ATC AND hides the
    // companion, so on OOS re-show it AFTER failClosed.
    // Custom paint on: the shopper is not buying the swatch colour underneath — swap the CART line (id/SKU only) to
    // the highest-inventory colour sibling of this config; every display above (price/gallery/specs/parts) stays
    // bound to the resolved variant. The legacy blank-SKU `Custom` backing value takes the same path. No sibling in
    // stock -> fail closed rather than let a wrong or un-fulfillable variant reach the cart.
    var cartV = (customPaintActive() || isCustomBackingColor(v.opts['Caliper Color'])) ? bestRealColorVariant(matrixCache[activeBacking.id], v) : v;
    if (!cartV || !cartV.available) { failClosed('OUT OF STOCK'); renderCompanion(v); populateViewThumbs(v); return; }
    var vid = numericId(cartV.id);
    setVariantId(vid);
    // The label must name the SKU the buyer is invoiced for. With Custom on that is the sibling, not the
    // swatch underneath — this store is ordered by SKU, so a label naming a SKU the order does not contain
    // is worse than the blank it used to show.
    if (cartV !== v) setSku(cartV.sku);
    root.dataset.resolvedSku = cartV.sku;
    root.dataset.resolvedId = vid; // resolved backing-variant id; kept in sync with [name=id] set above
    // Gate ATC on pad readiness: a warm-matrix resolve must NOT enable purchase before the resolved backing's pad
    // paints, else getSelected() harvests the PRIOR backing's pad onto the cart line.
    // updatePadOptions' success/persistent-fail calls releaseAtcForPads(id) to flip the button once pads settle.
    if (padsReadyFor === activeBacking.id) { setAtc(false, 'ADD TO CART'); atcHeldForPads = false; }
    else { setAtc(true, 'CHECKING…'); atcHeldForPads = true; }
    renderCompanion(v); // available: show the paired rear for the resolved variant (v.companion, variant-grain)
    // Record the resolved config + vehicle as cart line-item properties — fyre-pdp.js submit() harvests them.
    // Diameter is the per-backing axis (not always a variant option), so take it from the backing; rest from the variant.
    // Custom on: the swatch underneath is not the colour the shopper is buying — leave the key to the paint
    // panel's own Caliper Color / Paint Code inputs so the swatch never leaks onto the kit or paint line.
    setLineItemProps({
      'Caliper Color': customPaintActive() ? '' : v.opts['Caliper Color'],
      'Disc Diameter': (activeBacking && activeBacking.diameter) ? activeBacking.diameter + 'mm' : (v.opts['Disc Diameter'] || ''),
      'Disc Face': v.opts['Disc Type'],
      'Fits': fitmentDisplay()
    });
    notifyResolved(cartV.sku); // feed the CART-line SKU (what ships; the sibling when Custom is on) to the fitment-note module
  }

  // product-reference card payload (replacement_pad_product, content.disc_product) — title/url/image/price for an <a> card
  var PRODUCT_CARD = ' reference { ... on Product { title onlineStoreUrl featuredImage { url } priceRange { minVariantPrice { amount currencyCode } } } }';
  var MATRIX_QUERY =
    'query($id: ID!) @inContext(country: US) {' +
    ' product(id: $id) {' +
    '   handle' +
    '   featuredImage { url }' +  // the URL the Storefront API substitutes for an imageless variant — see ownsItsPhoto  // needed to hit the buyer-aware /products/<handle>.js price endpoint (Storefront price is retail-only)
    '   dp: metafield(namespace: "content", key: "disc_product") {' + PRODUCT_CARD + ' }' +
    '   fd: metafield(namespace: "specs", key: "fitment_diagram") { reference { ... on MediaImage { image { url } } } }' +
    // backing-constant disc specs (diameter/thickness/material/construction never vary within a backing; finish/mounting
    // are constant for non-Racing backings) — the product-level fallback when a variant carries no per-SKU value.
    '   dDia: metafield(namespace: "specs", key: "disc_diameter") { value }' +
    '   dThk: metafield(namespace: "specs", key: "disc_thickness") { value }' +
    '   dMat: metafield(namespace: "specs", key: "disc_material") { value }' +
    '   dCon: metafield(namespace: "specs", key: "disc_construction") { value }' +
    '   dFin: metafield(namespace: "specs", key: "disc_finish") { value }' +
    '   dMnt: metafield(namespace: "specs", key: "disc_mounting") { value }' +
    // caliper finish is backing-constant (finish-uniform per backing; 0 grain violations)
    '   cFin: metafield(namespace: "specs", key: "caliper_finish") { value }' +
    // Technical Specs line drawings. Backing-constant, and not always the umbrella's: GT|A 4-piston fronts span the
    // A, B and E caliper bodies, so the umbrella's drawing is wrong for part of its range.
    // Caliper body rows that differ within the GT|A 4-piston fronts (A/B/E bodies): mount type and bleed screw.
    '   cBody: metafield(namespace: "specs", key: "caliper_body_type") { value }' +
    '   cBleed: metafield(namespace: "specs", key: "bleed_screw") { value }' +
    '   cDraw: metafield(namespace: "specs", key: "caliper_drawing_svg") { reference { ... on MediaImage { image { url } } } }' +
    '   dDraw: metafield(namespace: "specs", key: "disc_drawing_svg") { reference { ... on MediaImage { image { url } } } }' +
    // RT Product Feed — the product-level fallback for variants carrying no feed of their own.
    '   pFeed: metafield(namespace: "custom", key: "rt_product_feed") { value }' +
    // Backing-constant client copy. The umbrella holds a union or a placeholder, never the resolved kit's text:
    // kit_includes is blank wherever its kits disagree, and short_description names one specific disc.
    '   kInc: metafield(namespace: "specs", key: "kit_includes") { value }' +
    '   sDesc: metafield(namespace: "specs", key: "short_description") { value }' +
    '   variants(first: 100) { nodes {' +
    '     id sku availableForSale quantityAvailable image { url }' +
    '     vFeed: metafield(namespace: "custom", key: "rt_feed") { value }' +
    '     price { amount currencyCode }' +
    '     selectedOptions { name value }' +
    // resolved-variant PDP images 2/3/4 (content.pdp_image_*) for the umbrella view gallery.
    '     vImg2: metafield(namespace: "content", key: "pdp_image_2") { reference { ... on MediaImage { image { url } } } }' +
    '     vImg3: metafield(namespace: "content", key: "pdp_image_3") { reference { ... on MediaImage { image { url } } } }' +
    '     vImg4: metafield(namespace: "content", key: "pdp_image_4") { reference { ... on MediaImage { image { url } } } }' +
    // per-variant disc specs (the SIX variant-grain fields) — the exact spec for the resolved SKU.
    '     vFace: metafield(namespace: "specs", key: "disc_type") { value }' +
    '     vGap: metafield(namespace: "specs", key: "disc_air_gap") { value }' +
    '     vAnn: metafield(namespace: "specs", key: "disc_annulus") { value }' +
    '     vVane: metafield(namespace: "specs", key: "disc_vane_design") { value }' +
    '     vFin: metafield(namespace: "specs", key: "disc_finish") { value }' +
    '     vMnt: metafield(namespace: "specs", key: "disc_mounting") { value }' +
    // paired rear umbrella per VARIANT — caliper-color splits (29 backings) pair to different rears.
    '     vComp: metafield(namespace: "specs", key: "companion_system") { reference { ... on Product { handle title featuredImage { url } } } }' +
    '     rpDiscLh: metafield(namespace: "specs", key: "replacement_disc_lh") { value }' +
    '     rpDiscRh: metafield(namespace: "specs", key: "replacement_disc_rh") { value }' +
    '     rpPadSet: metafield(namespace: "specs", key: "replacement_pad_set") { value }' +
    '     rpDiscHw: metafield(namespace: "specs", key: "replacement_disc_hardware") { value }' +
    '     rpBracket: metafield(namespace: "specs", key: "replacement_bracket") { value }' +
    '     rpBrakeLine: metafield(namespace: "specs", key: "replacement_brake_line") { value }' +
    '     rpDiscBell: metafield(namespace: "specs", key: "replacement_disc_bell") { value }' +
    '     rpPadProduct: metafield(namespace: "specs", key: "replacement_pad_product") {' + PRODUCT_CARD + ' }' +
    '   } } } }';

  // pull a product-reference card payload into a plain {title,url,image,price} (or null)
  function cardOf(mf) {
    var p = mf && mf.reference; if (!p) return null;
    var pr = p.priceRange && p.priceRange.minVariantPrice;
    return { title: p.title, url: p.onlineStoreUrl, image: p.featuredImage && p.featuredImage.url, price: pr || null };
  }
  // companion product_reference (rear umbrella) -> {handle,title,image} for the "Complete Your Brake System" card, or null.
  function compOf(mf) {
    var p = mf && mf.reference; if (!p) return null;
    return { handle: p.handle, title: p.title, image: p.featuredImage && p.featuredImage.url };
  }
  function val(mf) { return (mf && mf.value) || ''; }
  function imgRef(mf) { return (mf && mf.reference && mf.reference.image && mf.reference.image.url) || null; } // file_reference -> image url

  // Buyer-price overlay. The Storefront API (public token, @inContext country) is buyer-blind and returns RETAIL for
  // every variant, so a logged-in dealer would otherwise see retail on every resolved config. For a B2B buyer only,
  // re-source per-variant price from the buyer-aware /products/<handle>.js endpoint — the same one the B2B quick-order
  // form uses — which honors the logged-in company location's catalog (same-origin credentials carry the buyer session).
  // Returns a COPY with prices overlaid; the structural matrix stays retail + buyer-invariant so it is safe to persist.
  // Overlaid prices live in-memory only and are refreshed every session (never persisted) → they cannot leak across
  // sessions or go stale after a catalog change. Guests/retail (no catalog) are skipped — .js would just echo retail and
  // the extra fetch is pointless. Degrades to the structural (retail) matrix on any failure.
  function overlayBuyerPrices(handle, matrix) {
    if (!IS_B2B || !handle) return Promise.resolve(matrix);
    return fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        var variants = p && p.variants;
        if (!variants) return matrix;
        var byId = {}, hit = 0;
        variants.forEach(function (v) { byId[String(v.id)] = v.price; }); // buyer-context price, in cents
        var out = matrix.map(function (m) {
          var cents = byId[numericId(m.id)];
          if (cents == null) return m; // variant absent from .js → keep structural (retail) price, never fabricate
          hit++;
          // String amount ((cents/100).toFixed(2)) mirrors the Storefront money shape so both code paths are identical downstream.
          return assign(m, { price: { amount: (cents / 100).toFixed(2), currencyCode: (m.price && m.price.currencyCode) || 'USD' } });
        });
        // partial coverage = publication/availability skew between the public Storefront set and the buyer's .js set;
        // those rows show retail. Surface it rather than silently mixing dealer + retail prices in one matrix.
        if (hit < matrix.length) console.warn('[fyre-resolve] buyer-price coverage ' + hit + '/' + matrix.length + ' for ' + handle + ' — uncovered variants show retail.');
        return out;
      })
      .catch(function () { return matrix; });
  }
  // shallow clone + override (avoids mutating the persisted structural matrix; price is the only field replaced).
  function assign(obj, over) { var c = {}; for (var k in obj) { if (Object.prototype.hasOwnProperty.call(obj, k)) c[k] = obj[k]; } for (var j in over) { if (Object.prototype.hasOwnProperty.call(over, j)) c[j] = over[j]; } return c; }

  // Build (or read from cache) the buyer-INVARIANT structural matrix (options/image/specs + Storefront RETAIL price)
  // for a backing, plus its handle, and persist. Buyer prices are NOT here — loadMatrix overlays them live.
  function loadStructural(backing) {
    if (matrixCache[backing.id]) return Promise.resolve(matrixCache[backing.id]);
    return sf(MATRIX_QUERY, { id: backing.id }).then(function (d) {
      var prod = d.product || {};
      discProductCache[backing.id] = cardOf(prod.dp); // product-level disc product (same for all variants)
      fitmentDiagramCache[backing.id] = (prod.fd && prod.fd.reference && prod.fd.reference.image && prod.fd.reference.image.url) || null;
      // RT Product Feed gate — drop the SKUs this buyer may not see BEFORE the matrix exists, so no downstream
      // consumer (swatches, option availability, resolution, cart) can surface one. A backing left with no visible
      // variant yields an empty matrix, which resolves to the existing unavailable path.
      var pFeed = prod.pFeed && prod.pFeed.value;
      var nodes = ((prod.variants && prod.variants.nodes) || []).filter(function (n) {
        // Gate script missing (404, blocked, load-order break). Backing SKUs come from the Storefront API and are
        // never Liquid-rendered, so nothing else gates them — fail CLOSED for retail rather than exposing every
        // B2B-only SKU. A B2B buyer may see everything, so failing open costs them nothing.
        if (!window.FyreRtFeed) return IS_B2B_CUSTOMER;
        return window.FyreRtFeed.visible(n.vFeed && n.vFeed.value, pFeed, IS_B2B_CUSTOMER);
      });
      var featuredUrl = (prod.featuredImage && prod.featuredImage.url) || null;
      var matrix = nodes.map(function (n) {
        var opts = {}; (n.selectedOptions || []).forEach(function (o) { opts[o.name] = o.value; });
        var rawPhoto = n.image && n.image.url;
        var ownPhoto = ownsItsPhoto(rawPhoto, featuredUrl, opts['Disc Type']) ? rawPhoto : null;
        return {
          id: n.id, sku: n.sku, available: n.availableForSale,
          qty: typeof n.quantityAvailable === 'number' ? n.quantityAvailable : null,
          price: n.price, image: ownPhoto, opts: opts,
          images: [ownPhoto, imgRef(n.vImg2), imgRef(n.vImg3), imgRef(n.vImg4)].filter(Boolean), // [Main, PDP img 2/3/4] for the resolved-variant view gallery
          companion: compOf(n.vComp), // variant-grain paired rear umbrella (rides matrixCache -> persisted; no separate cache)
          specs: { // per-variant disc specs (keyed by data-spec cell key) for renderTechSpecs
            disc_type: val(n.vFace), disc_air_gap: val(n.vGap), disc_annulus: val(n.vAnn),
            disc_vane_design: val(n.vVane), disc_finish: val(n.vFin), disc_mounting: val(n.vMnt)
          },
          repl: { // replacement parts for the Replacement Parts panel. text SKUs, except pad_product = card.
            disc_lh: val(n.rpDiscLh), disc_rh: val(n.rpDiscRh), pad_set: val(n.rpPadSet), disc_hw: val(n.rpDiscHw),
            bracket: val(n.rpBracket), brake_line: val(n.rpBrakeLine), disc_bell: val(n.rpDiscBell), pad_product: cardOf(n.rpPadProduct)
          }
        };
      });
      // backing-constant disc specs = PRODUCT-level fallback for cells with no per-variant value.
      // diameter/thickness/material/construction never vary within a backing → always safe to fall back.
      // finish/mounting are safe ONLY when NO variant carries them (non-Racing backings). Racing backings vary
      // finish/mounting per variant AND hold a single product-level value that is WRONG for most variants (e.g.
      // product disc_finish "Corrosion Resistant Plating" over Unplated/CRP variants) — so drop the fallback there.
      // A blank variant then falls through to the umbrella default, never a confidently-wrong spec.
      backingSpecsCache[backing.id] = {
        disc_diameter: val(prod.dDia), disc_thickness: val(prod.dThk), disc_material: val(prod.dMat),
        disc_construction: val(prod.dCon),
        disc_finish: matrix.some(function (v) { return v.specs.disc_finish; }) ? '' : val(prod.dFin),
        disc_mounting: matrix.some(function (v) { return v.specs.disc_mounting; }) ? '' : val(prod.dMnt),
        caliper_finish: val(prod.cFin), // backing-constant; renderTechSpecs writes the [data-spec="caliper_finish"] cell
        kit_includes: val(prod.kInc),
        short_description: val(prod.sDesc),
        caliper_body_type: val(prod.cBody),
        bleed_screw: val(prod.cBleed),
        caliper_drawing: imgRef(prod.cDraw),
        disc_drawing: imgRef(prod.dDraw)
      };
      matrixCache[backing.id] = matrix;             // structural + retail — buyer-invariant, safe to persist
      matrixHandle[backing.id] = prod.handle || ''; // for the live buyer-price overlay
      persistCache();
      return matrix;
    });
  }

  // Resolve a backing to its DISPLAY matrix: the structural matrix with buyer-aware prices overlaid live (B2B only,
  // in-memory, refreshed each session). Guests/retail get the structural (retail) matrix untouched.
  function loadMatrix(backing) {
    if (pricedCache[backing.id]) return Promise.resolve(pricedCache[backing.id]);
    return loadStructural(backing).then(function (m) {
      return overlayBuyerPrices(matrixHandle[backing.id], m).then(function (priced) {
        pricedCache[backing.id] = priced;
        return priced;
      });
    });
  }

  // ── Brake-pad options ─────────────────────────────────────────────────────────────────────────────
  // The static brake_pad_picker (fyre-template-product.liquid) renders the umbrella's product-level
  // content.pad_products — a UNION of every backing's pad across the whole diameter range. It's a COMPOUND
  // chooser, so one compound (e.g. Brembo FM1000) that exists in several pad SHAPES renders as duplicate
  // pills ("FM1000 x3"). The resolved backing carries its OWN content.pad_products (one shape, correct);
  // fetch that per backing and let FyrePadPicker (fyre-pdp.js) rebuild the pills. Deliberately a SEPARATE
  // query (not MATRIX_QUERY): the pill needs pad sku + variant id, which are NOT on the persisted matrix —
  // adding them there would force a CACHE_KEY bump. padCache is in-memory only, mirroring pricedCache.
  var PAD_QUERY =
    'query($id: ID!) @inContext(country: US) {' +
    ' product(id: $id) {' +
    '   pads: metafield(namespace: "content", key: "pad_products") {' +
    '     references(first: 20) { nodes { ... on Product {' +
    '       title' +
    '       priceRange { minVariantPrice { amount currencyCode } }' +
    '       variants(first: 1) { nodes { id sku availableForSale } }' +
    '     } } }' +
    '   }' +
    '   dflt: metafield(namespace: "specs", key: "default_pad_set") {' +
    '     reference { ... on Product {' +
    '       title' +
    '       priceRange { minVariantPrice { amount currencyCode } }' +
    '       variants(first: 1) { nodes { id sku } }' +
    '     } }' +
    '   }' +
    ' }' +
    '}';

  // Storefront pad-references payload -> [{title, sku, variantId(numeric), price, available}] the pill markup
  // needs. variantId is normalized to the NUMERIC form (matches the static Liquid pill's data-pad-variant-id, so
  // FyrePadPicker.getSelected's parseInt stays valid — no GID NaN).
  function padsOf(d) {
    var refs = d && d.product && d.product.pads && d.product.pads.references && d.product.pads.references.nodes;
    if (!refs || !refs.length) return [];
    return refs.map(function (p) {
      var v = (p.variants && p.variants.nodes && p.variants.nodes[0]) || null;
      var pr = p.priceRange && p.priceRange.minVariantPrice;
      return { title: p.title || '', sku: (v && v.sku) || '', variantId: v ? numericId(v.id) : '', price: pr ? pr.amount : '', available: v ? !!v.availableForSale : false };
    });
  }

  // The backing's specs.default_pad_set — the pad already priced into the kit BOM. The swap credit is computed
  // downstream from this, so it must come from the RESOLVED backing, never the umbrella (whose union spans shapes).
  function defaultOf(d) {
    var r = d && d.product && d.product.dflt && d.product.dflt.reference;
    if (!r) return null;
    var v = (r.variants && r.variants.nodes && r.variants.nodes[0]) || null;
    var pr = r.priceRange && r.priceRange.minVariantPrice;
    // Price rides along unused by the cart today — NetSuite credits the kit pad by hand and knows its own cost.
    // It is kept because a "+$X to swap" price delta on the pill is the obvious next ask and the field is free.
    return { title: r.title || '', sku: (v && v.sku) || '', variantId: v ? numericId(v.id) : '', price: pr ? pr.amount : '' };
  }

  // resolved backing -> its own pad options (in-memory cache, keyed on backing id — never persisted).
  // Retries the transient Storefront throttle: PAD_QUERY fires in the same boot burst as GRAPH_QUERY, so the
  // shared rate bucket can 430 it — without a backoff the fail path (static pills + null harvest) becomes the
  // LIKELY path, not an edge. Mirrors the compat query's attempt(3, 700) backoff.
  function loadPadOptions(backing) {
    if (padCache[backing.id]) return Promise.resolve(padCache[backing.id]);
    function attempt(retriesLeft, delay) {
      return sf(PAD_QUERY, { id: backing.id }, 8000).catch(function (e) { // 8s abort so a hung fetch can't wedge ATC
        if (retriesLeft <= 0) throw e;
        return new Promise(function (r) { setTimeout(r, delay); }).then(function () { return attempt(retriesLeft - 1, delay * 2); });
      });
    }
    return attempt(3, 700).then(function (d) {
      var pads = padsOf(d);
      pads.defaultPad = defaultOf(d); // rides on the array so padCache stays one keyed entry
      padCache[backing.id] = pads;
      return pads;
    });
  }

  // tell FyrePadPicker how to render (CustomEvent decoupling — no direct cross-module call). state:
  //   'pending' — a new backing is resolving; picker blocks harvest until its pads paint
  //   'ready'   — paint the resolved backing's shape-correct pads
  //   'empty'   — resolved backing has NO pads; picker hides (don't show the umbrella union as fake offers)
  //   'failed'  — pad fetch failed after retries; picker hides (no false checked pill), harvest stays null
  //   'static'  — no backing / pre-resolution; restore the static union pills
  function dispatchPad(state, pads, backingId) {
    var dflt = (pads && pads.defaultPad) || null;
    try { window.dispatchEvent(new CustomEvent('fyre:pad-options', { detail: { state: state, pads: pads || null, defaultPad: dflt, backingId: backingId || null } })); } catch (e) {}
  }
  // no resolved backing (fail-branch / matrix error) -> picker falls back to the static pills. Bumps padSeq so
  // any in-flight pad fetch for a prior backing cannot repaint over the revert; clears the ATC pad-gate.
  function revertPads() { padSeq++; padsReadyFor = null; dispatchPad('static', null, null); }
  // release the ATC gate once the resolved backing's pad state settles (paint or persistent-fail), so a warm-cache
  // resolve that was held at applyResolution can now enable purchase. Only when this backing is still the active one.
  function releaseAtcForPads(id) {
    padsReadyFor = id;
    // Enable ATC only when the form actually HOLDS the resolved variant — root.dataset.resolvedId is set at a clean
    // resolve (applyResolution) and cleared on any switch/fail (onVehicle/failClosed). Without this, a warm pad
    // fetch that lands before a cold matrix, while atcHeldForPads still leaks true from the PRIOR backing, would
    // flip ATC to 'ADD TO CART' with a blank [name=id] (id=NaN cart-add) before applyResolution runs.
    if (atcHeldForPads && activeBacking && activeBacking.id === id && root.dataset.resolvedId) { setAtc(false, 'ADD TO CART'); atcHeldForPads = false; }
  }
  // fired from setActiveBacking on every backing change. Guards: generation (padSeq) + backing-still-active at
  // dispatch. Marks pads NOT-ready synchronously (padsReadyFor=null) so applyResolution holds ATC through the
  // fetch window, and signals 'pending' so the picker won't harvest the prior backing's pad.
  function updatePadOptions(backing) {
    if (!backing) { revertPads(); return; }
    var mySeq = ++padSeq, id = backing.id;
    padsReadyFor = null;
    dispatchPad('pending', null, id);
    loadPadOptions(backing).then(function (pads) {
      if (mySeq !== padSeq || !activeBacking || activeBacking.id !== id) return; // superseded
      dispatchPad((pads && pads.length) ? 'ready' : 'empty', pads || [], id);
      releaseAtcForPads(id);
    }).catch(function (e) {
      if (mySeq === padSeq && activeBacking && activeBacking.id === id) {
        // persistent fail (already retried): don't block the sale over a property-only pad — hide the picker (no
        // false selection) and release the ATC gate. getSelected() stays null so no wrong pad rides the cart line.
        dispatchPad('failed', null, id);
        releaseAtcForPads(id);
      }
      console.error('[fyre-resolve] pad-options', e);
    });
  }

  // The download link serves the same-stem source PDF (prints to scale), not the display PNG:
  // .../1L_.8018A_DRW.png?v=1 and .../1Bx.7009A_DRW_<uuid>.png → .../<stem>_DRW.pdf. No _DRW → the url unchanged.
  function fitmentPdfUrl(url) {
    return url.replace(/(_DRW)[^/?]*\.(png|webp|jpe?g)(\?.*)?$/, '$1.pdf');
  }

  // Umbrella Wheel Fitment panel. Unresolved (no vehicle, no fit, no diameter): the "select your vehicle" prompt,
  // because the umbrella's own diagram is the representative kit's and would mislead. Resolved with a diagram: that
  // diagram. Resolved without one: Image Coming Soon — the system is known, its drawing is not.
  function showFitmentDiagram(url, resolved) {
    var diagImg = document.getElementById('fyre-fitment-diagram-img');
    var prompt = document.querySelector('[data-wheels-prompt]');
    var src = url || (resolved ? placeholderUrl() : '');
    if (diagImg) {
      if (src) { diagImg.src = src; diagImg.style.display = ''; }
      else { diagImg.removeAttribute('src'); diagImg.style.display = 'none'; }
    }
    if (prompt) prompt.style.display = src ? 'none' : '';
    // the diagram itself downloads the PDF too; the Image Coming Soon placeholder links nowhere
    var imgLink = document.getElementById('fyre-fitment-diagram-img-link');
    if (imgLink) {
      // hidden with its image, or the empty block squeezes the row-flex prompt beside it
      imgLink.style.display = src ? '' : 'none';
      if (url) { imgLink.href = fitmentPdfUrl(url); imgLink.setAttribute('aria-label', 'Download wheel fitment template (PDF)'); }
      else { imgLink.removeAttribute('href'); imgLink.removeAttribute('aria-label'); }
    }
    ['fyre-fitment-diagram-download-pdp-info', 'fyre-fitment-diagram-download-pdp-fitment'].forEach(function (id) {
      var a = document.getElementById(id);
      if (!a) return;
      if (url) { a.href = fitmentPdfUrl(url); a.style.display = ''; }
      else { a.style.display = 'none'; }
    });
  }

  // single owner of activeBacking: every write re-renders the Vehicle Compatibility panel and refreshes the pad
  // options for that backing. Color/Disc picks keep the same backing → no re-render.
  function setActiveBacking(b) { activeBacking = b; renderVehicleCompat(); renderResolvedCopy(); updatePadOptions(b); }

  // vehicle OR diameter changed (or initial): pick the backing by (vehicle, picked diameter), load matrix, resolve.
  // Diameter is the product/backing axis in Model F — one vehicle can fit several per-diameter backings, so the
  // Diameter pick (not vehicle alone) selects the backing. Re-entered on diameter change via the option handler.
  function onVehicle() {
    driveFitmentBar();
    if (!graph) { setAtc(true, 'LOADING…'); return; }
    setVariantId(''); delete root.dataset.resolvedSku; delete root.dataset.resolvedId;
    var vh = ambientVehicleHandle();
    // Every fail branch below must reset BOTH the backing state (setActiveBacking re-renders Vehicle Compat;
    // showFitmentDiagram clears the wheel diagram) AND the spec/replacement-parts display (restoreSpecDefaults
    // plus a contextual note), or the prior vehicle's data stays on screen.
    // No vehicle: pass null, which ENABLES every option. The representative matrix is a 4-backing sample and is
    // not evidence of what can be built — see the note in finishRepresentative. Until a vehicle narrows the
    // backings to a complete fitting set, the umbrella's own picker is the authority and nothing is gated.
    if (!vh) { setActiveBacking(null); applyOptionAvailability(null);
       showFitmentDiagram(null); restoreSpecDefaults(); restorePriceSkuDefaults(); if (representativeMatrix) setInitialGallery(representativeMatrix); setReplacementsNote('Select your vehicle above to see replacement parts.'); failClosed('SELECT VEHICLE FIRST'); return companionNote('Select your vehicle above to see the matching rear system.'); }
    var fitting = candidates(vh); // every backing fitting this vehicle, across diameters
    if (fitting.length === 0) { setActiveBacking(null); applyOptionAvailability(null); showFitmentDiagram(null); restoreSpecDefaults(); restorePriceSkuDefaults(); if (representativeMatrix) setInitialGallery(representativeMatrix); setReplacementsNote('This system does not fit the selected vehicle.'); return failClosed('DOES NOT FIT THIS VEHICLE'); }
    var fitDias = {}; fitting.forEach(function (b) { if (b.diameter) fitDias[b.diameter] = true; });
    var diaPick = ensureDiameterPickFits(fitDias); // disable non-fitting diameters + auto-correct the checked one
    var match = fitting.filter(function (b) { return !b.diameter || !diaPick || b.diameter === diaPick; });
    if (match.length === 0) { setActiveBacking(null); applyOptionAvailability(null); showFitmentDiagram(null); restoreSpecDefaults(); restorePriceSkuDefaults(); if (representativeMatrix) setInitialGallery(representativeMatrix); setReplacementsNote('Select a diameter to see replacement parts.'); return failClosed('SELECT DIAMETER'); }
    if (match.length > 1) console.warn('[fyre-resolve] ' + match.length + ' backings for ' + vh + '/' + diaPick + ' — using first', match.map(function (b) { return b.id; }));
    setActiveBacking(match[0]);
    hideCompanion(); // companion is variant-grain — render only after applyResolution resolves the variant (no prior-variant rear)
    var seq = ++resolveSeq;
    setAtc(true, 'CHECKING…');
    // Reset disc-spec cells to the umbrella default the moment a new backing starts resolving — otherwise the
    // prior vehicle's EXACT specs stay on screen through the fetch, a hang, or a failed load (failClosed below
    // never restores them). renderTechSpecs re-fills on a clean resolve.
    restoreSpecDefaults();
    // Same reasoning as restoreSpecDefaults above, applied to the gallery: the thumbs on screen belong to the
    // PREVIOUS vehicle and stay clickable through this fetch. Drop them now; the resolve repaints below.
    clearViewThumbs();
    loadMatrix(activeBacking).then(function (matrix) {
      if (seq !== resolveSeq) return;
      applyOptionAvailability(matrix);
      populateSwatches(matrix);
      applyResolution();
      showFitmentDiagram(fitmentDiagramCache[activeBacking.id] || null, true);
      // re-sync once more after any option auto-correct's own deferred resolve has settled (fixes an
      // occasional stale thumbnail count on first load)
      setTimeout(function () { if (seq === resolveSeq && activeBacking && matrixCache[activeBacking.id]) populateThumbnails(matrixCache[activeBacking.id]); }, 0);
    }).catch(function (e) { if (seq === resolveSeq) { failClosed('BUILDER UNAVAILABLE'); revertPads(); } console.error('[fyre-resolve] matrix', e); });
  }

  function bind() {
    // a Diameter pick changes the BACKING (product axis) → re-run backing selection; Color/Disc resolve within it
    optionRadios().forEach(function (r) { r.addEventListener('change', function () { setTimeout(isDiameterRadio(r) ? onVehicle : applyResolution, 0); }); });
    // Custom paint toggled after a resolve must re-pick the cart line (highest-inventory sibling <-> the swatch).
    var customToggle = document.querySelector('[data-custom-color-toggle]');
    if (customToggle) customToggle.addEventListener('change', function () { setTimeout(applyResolution, 0); });
    window.addEventListener('fitment:changed', function () { setTimeout(onVehicle, 0); });
    // If FyrePadPicker registers its listener AFTER the initial pad dispatch (warm-cache init-order race — benign
    // while padCache is in-memory so the dispatch trails an async fetch, but a pre-warm would reintroduce the
    // duplicate pills), let a late picker pull the current state. Mirrors the fyre:resolved seed.
    window.addEventListener('fyre:pad-options-request', function () { if (activeBacking) updatePadOptions(activeBacking); });
    // Pad choice changed -> re-render the resolved backing's price with the new swap delta. No-op before a
    // backing resolves: until then the umbrella's own server-rendered price stands and owns the element.
    // The paint surcharge moved without a re-resolve (a Stage 3 colour, or the paint code clearing one).
    // fyre-pdp.js announces it rather than writing .pdp-price itself; this resolver is the sole price writer
    // on an umbrella, and setPrice re-reads the surcharge live.
    window.addEventListener('fyre:paint-surcharge', function () { if (lastResolvedPrice) setPrice(lastResolvedPrice); });
    window.addEventListener('fyre:pad-delta', function (e) {
      var d = e && e.detail ? Number(e.detail.delta) : 0;
      padDelta = isFinite(d) ? d : 0;
      if (lastResolvedPrice) setPrice(lastResolvedPrice);
    });
  }

  // ATC is gated SOLELY by the fail-closed button-disable state machine — there is no submit-time interceptor.
  // setAtc(false) fires only on a clean resolve in applyResolution(), immediately after setVariantId(vid) writes
  // the resolved backing variant into [name=id], so the button is clickable ONLY when the form holds the resolved
  // variant. Every non-resolved path calls failClosed()/setAtc(true, …), keeping it disabled. This resolver is the
  // sole owner of [name=id]; fyre-pdp.js never rewrites it after a resolve.
  setVariantId('');
  setAtc(true, 'LOADING…');
  var GRAPH_QUERY =
    'query($id: ID!, $cursor: String) {' +
    ' collection(id: $id) { products(first: 250, after: $cursor) {' +
    '   pageInfo { hasNextPage endCursor }' +
    '   nodes {' +
    '   id updatedAt' +
    '   dia: metafield(namespace: "specs", key: "disc_diameter") { value }' +
    '   fg: metafield(namespace: "fitment", key: "groups") {' +
    // list.metaobject_reference caps at 127 items → first:128 can never truncate the group list
    '     references(first: 128) { nodes { ... on Metaobject {' +
    '       id' +
    '       vehicles: field(key: "vehicles") { references(first: 250) { nodes { ... on Metaobject { handle } } } }' +
    '     } } }' +
    '   }' +
    ' } } } }';

  // lightweight signature query — revalidates the cache WITHOUT refetching the heavy nested graph
  var SIG_QUERY = 'query($id: ID!, $cursor: String) { collection(id: $id) { products(first: 250, after: $cursor) { pageInfo { hasNextPage endCursor } nodes { id updatedAt } } } }';
  function sigFromNodes(nodes) { return (nodes || []).map(function (n) { return n.id + ':' + (n.updatedAt || ''); }).sort().join('|'); }

  function applyGraphNodes(nodes) {
    nodes = nodes || [];
    // collection.products is cursor-paginated in fetchAllNodes, so `nodes` carries the FULL backing set (flagship
    // families run 268 > the 250 page cap). The loop guarantees completeness — no truncation warn needed.
    graph = nodes.map(function (p) {
      var groups = (p.fg && p.fg.references && p.fg.references.nodes) || [];
      if (groups.length >= 128) console.warn('[fyre-resolve] fitment-group at the 128 (list.metaobject_reference 127) ceiling on ' + p.id + '.');
      var vehicles = {}, groupIds = [];
      groups.forEach(function (g) {
        if (g.id) groupIds.push(g.id); // the compat panel joins backing → group on this id
        var vn = (g.vehicles && g.vehicles.references && g.vehicles.references.nodes) || [];
        if (vn.length >= 250) console.warn('[fyre-resolve] vehicle cap (250) hit in a group on ' + p.id + ' — some vehicles unreachable.');
        vn.forEach(function (v) { vehicles[v.handle] = true; });
      });
      return { id: p.id, diameter: normDia(p.dia && p.dia.value), vehicles: vehicles, groupIds: groupIds };
    });
  }

  var bound = false;
  function refresh() {
    if (!bound) { bind(); bound = true; }      // bind listeners ONCE (cache-boot + revalidate must not double-bind)
    onVehicle();                               // drives Vehicle Compatibility via setActiveBacking
    // caliper swatches (+ gallery without a vehicle) from a representative backing — no grey placeholders.
    if (graph && graph.length) loadRepresentative(0);
  }
  // One backing rarely carries every disc face the umbrella's picker offers, and a face with no row of its own
  // cannot change the gallery image. So MERGE nodes (same cap of 4) until every picker face is represented by a
  // photographed row, then hand the merged matrix to the gallery and to the pre-vehicle availability pass.
  function loadRepresentative(i, acc) {
    acc = acc || [];
    if (!graph || i >= graph.length || i >= 4) { finishRepresentative(acc, true); return; }
    loadMatrix(graph[i]).then(function (matrix) {
      var next = acc.concat(matrix);
      if (next.some(function (v) { return v.image; }) && !missingDiscFaces(next).length) { finishRepresentative(next, false); return; }
      loadRepresentative(i + 1, next);
    }).catch(function () { loadRepresentative(i + 1, acc); });
  }
  function finishRepresentative(matrix, exhausted) {
    if (!matrix.length || !matrix.some(function (v) { return v.image; })) return;
    representativeMatrix = matrix;
    populateSwatches(matrix);
    if (exhausted) {
      var missing = missingDiscFaces(matrix);
      if (missing.length) console.warn('[fyre-resolve] no photographed row for disc face(s): ' + missing.join(', ') + ' — those faces hold the current gallery image.');
    }
    // show the representative gallery whenever nothing's resolved yet — not just "no vehicle at all"
    // (a non-fitting vehicle also leaves activeBacking null)
    if (!activeBacking) {
      // NO PRE-VEHICLE GATING (Paul, 2026-09-11, after OR-4).
      //
      // This used to read `if (!ambientVehicleHandle()) applyOptionAvailability(matrix)`, on the theory that the
      // merged representative matrix showed which option values exist at all. It does not: loadRepresentative
      // stops at FOUR backings, and a builder family runs to dozens — 57 on the PISTA 6-piston 20D890 umbrella.
      // Greying an option on a 4-of-57 sample asserts "you cannot build this" from 7% of the evidence, and it
      // was wrong in practice: TY3L rendered disabled on umbrellas whose later backings do offer it.
      //
      // The two grains are the point. The PICKER is rendered from the UMBRELLA's own RT-feed-filtered variants —
      // complete, server-side, no sampling — so pre-vehicle it is already the authoritative list of what we
      // sell. The BACKINGS only become authoritative once a vehicle narrows them to a complete fitting set,
      // which is what the post-vehicle calls below do. Gating the umbrella's list with a backing sample mixed
      // the two and could only ever subtract.
      setInitialGallery(matrix);
    }
  }

  // The persisted matrices are RT-Product-Feed filtered, so they are specific to the buyer who cached them. A
  // dealer and a retail shopper share one browser profile often enough (a rep testing the storefront logged out)
  // that the flag must be checked, not assumed — a mismatch drops the cache and refetches rather than leaking.
  function readCache() { try { var c = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null'); if (c && c.b2b !== IS_B2B_CUSTOMER) return null; return (c && c.graph) ? c : null; } catch (e) { return null; } }
  function persistCache() { try { localStorage.setItem(CACHE_KEY, JSON.stringify({ sig: currentSig, b2b: IS_B2B_CUSTOMER, graph: graph, matrices: matrixCache, handles: matrixHandle, disc: discProductCache, fd: fitmentDiagramCache, bs: backingSpecsCache })); } catch (e) {} }
  function defer(fn) { if (window.requestIdleCallback) { window.requestIdleCallback(fn, { timeout: 2500 }); } else { setTimeout(fn, 300); } }

  // Cursor-paginate a collection.products query to exhaustion. Flagship builder collections hold 268 backings > the
  // 250 page cap, so a single page silently drops the tail (350mm marquee vehicles — Corvette/WRX/Evo/Golf R).
  // BOTH GRAPH_QUERY and SIG_QUERY must use this: if the graph is 268 nodes but the sig reads only 250, the signature
  // mismatches on every page view → the heavy graph refetches forever.
  function fetchAllNodes(query) {
    var acc = [];
    function page(cursor) {
      return sf(query, { id: COLLECTION_GID, cursor: cursor }).then(function (d) {
        var conn = d && d.collection && d.collection.products;
        acc = acc.concat((conn && conn.nodes) || []);
        var pi = conn && conn.pageInfo;
        return (pi && pi.hasNextPage) ? page(pi.endCursor) : acc;
      });
    }
    return page(null);
  }

  function fetchFullGraph(knownSig) {
    return fetchAllNodes(GRAPH_QUERY).then(function (nodes) {
      applyGraphNodes(nodes);
      matrixCache = {}; matrixHandle = {}; pricedCache = {}; padCache = {}; discProductCache = {}; fitmentDiagramCache = {}; backingSpecsCache = {};  // membership/data changed -> drop stale caches (padCache too: a pad_products edit bumps the sig)
      currentSig = knownSig || sigFromNodes(nodes);
      refresh();
      persistCache();
    }).catch(function (e) { if (!graph) { console.error('[fyre-resolve] graph', e); failClosed('BUILDER UNAVAILABLE'); } });
  }

  // 1) paint instantly from cache; 2) defer a cheap signature check; refetch the heavy graph only if it changed.
  var cached = readCache();
  if (cached) {
    graph = cached.graph; matrixCache = cached.matrices || {}; matrixHandle = cached.handles || {}; discProductCache = cached.disc || {}; fitmentDiagramCache = cached.fd || {}; backingSpecsCache = cached.bs || {}; currentSig = cached.sig;
    refresh();
    defer(function () {
      fetchAllNodes(SIG_QUERY).then(function (nodes) {
        var sig = sigFromNodes(nodes);
        if (sig !== currentSig) fetchFullGraph(sig);  // size/state differs -> invalidate + refetch the big graph
      }).catch(function () {});
    });
  } else {
    fetchFullGraph(null);                            // first visit: full fetch, boot, cache
  }
})();
